import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import API from '../config/api';
import QuratorLayout from '../components/qurator/QuratorLayout';
import { analyticsPdfFilename, downloadAnalyticsPdf } from '../utils/analyticsReportExport';
import '../styles/analytics.css';

/** Set true to show the explicit Enlarge button on deviation proof images (click-to-enlarge still works). */
const SHOW_PROOF_ENLARGE_BUTTON = false;

/* ─── Portal Tooltip (same as Dashboard) ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`anl-tooltip anl-tooltip-${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

/* ─── Verbatim notebook + stepwise deviations (shared helpers) ─── */
function rebuildDetailedNotebookClient(parsedStepsData) {
  if (!parsedStepsData || typeof parsedStepsData !== 'object') return [];
  const stepKeys = Object.keys(parsedStepsData)
    .map((k) => parseInt(k, 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
  const out = [];
  for (const stepNum of stepKeys) {
    const sd = parsedStepsData[stepNum] ?? parsedStepsData[String(stepNum)];
    if (!sd) continue;
    const chunks = [];
    const entries = sd.entries || [];
    function addSystemLine(line) {
      const last = chunks[chunks.length - 1];
      if (last && last.kind === 'system') last.lines.push(line);
      else chunks.push({ kind: 'system', lines: [line] });
    }
    let currentCycleLines = null;
    for (const ent of entries) {
      if (!ent || !ent.type) continue;
      if (ent.type === 'system') {
        if (currentCycleLines && currentCycleLines.length) {
          chunks.push({ kind: 'cycle', lines: currentCycleLines });
          currentCycleLines = null;
        }
        const t = ent.time != null ? ent.time : '??:??:??';
        const msg = ent.content != null ? String(ent.content) : '';
        addSystemLine(`[RealtimeTraineeAnalyzer] [${t}] [STEP: ${stepNum}] ${msg}`);
      } else if (ent.type === 'prompt') {
        if (currentCycleLines && currentCycleLines.length) {
          chunks.push({ kind: 'cycle', lines: currentCycleLines });
          currentCycleLines = null;
        }
        const txt = typeof ent.content === 'string' ? ent.content : '';
        currentCycleLines = txt ? txt.split('\n') : ['(prompt)'];
      } else if (ent.type === 'response') {
        const respStr =
          typeof ent.content === 'object' && ent.content !== null
            ? `Raw response:\n${JSON.stringify(ent.content, null, 2)}`
            : `Raw response: ${String(ent.content)}`;
        if (!currentCycleLines) currentCycleLines = [];
        currentCycleLines.push(respStr);
        chunks.push({ kind: 'cycle', lines: currentCycleLines });
        currentCycleLines = null;
      }
    }
    if (currentCycleLines && currentCycleLines.length) {
      chunks.push({ kind: 'cycle', lines: currentCycleLines });
    }
    out.push({ step_number: stepNum, chunks });
  }
  return out;
}

function getDetailedNotebookForDisplay(metrics) {
  if (Array.isArray(metrics.detailed_notebook) && metrics.detailed_notebook.length > 0) {
    return metrics.detailed_notebook;
  }
  if (metrics.parsed_steps_data) {
    return rebuildDetailedNotebookClient(metrics.parsed_steps_data);
  }
  return [];
}

/** Merge cycles for the same PROMPT[step N] (matches backend mergeStructuredActions). */
function mergeStructuredActionsClient(rawActions) {
  if (!Array.isArray(rawActions) || rawActions.length === 0) return [];
  const buckets = new Map();
  let nullKeySeq = 0;
  for (const a of rawActions) {
    const p = a.prompt_step_index;
    let key;
    if (p != null && Number.isFinite(Number(p)) && Number(p) > 0) {
      key = `p:${Number(p)}`;
    } else {
      key = `n:${a.ordinal != null ? a.ordinal : ++nullKeySeq}`;
    }
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(a);
  }
  const merged = [];
  for (const [, items] of buckets) {
    items.sort((x, y) => {
      const wx = x.wall_time_sec;
      const wy = y.wall_time_sec;
      if (wx == null && wy == null) return 0;
      if (wx == null) return 1;
      if (wy == null) return -1;
      return wx - wy;
    });
    let verdict = null;
    for (const it of items) {
      if (it.verdict != null) verdict = it.verdict;
    }
    const logs = items.map((it) => it.verbatim_log).filter((s) => s && String(s).trim());
    const mergedLog = logs.join('\n\n────────\n\n');
    const wallTimes = items
      .map((it) => it.wall_time_sec)
      .filter((t) => t != null && !Number.isNaN(Number(t)));
    const wallMin = wallTimes.length ? Math.min(...wallTimes) : null;
    const base = items[0];
    const label =
      items.map((it) => it.label).find((l) => l && String(l).trim()) || base.label;
    const step_context =
      items.map((it) => it.step_context).find((s) => s && String(s).trim()) ||
      base.step_context ||
      '';
    merged.push({
      ...base,
      label,
      step_context,
      verdict,
      verbatim_log: mergedLog,
      wall_time_sec: wallMin,
      prompt_step_index: base.prompt_step_index,
      ordinal: 0
    });
  }
  merged.sort((a, b) => {
    const pa = a.prompt_step_index != null ? Number(a.prompt_step_index) : Infinity;
    const pb = b.prompt_step_index != null ? Number(b.prompt_step_index) : Infinity;
    if (pa !== pb) return pa - pb;
    const wa = a.wall_time_sec != null ? Number(a.wall_time_sec) : 0;
    const wb = b.wall_time_sec != null ? Number(b.wall_time_sec) : 0;
    return wa - wb;
  });
  merged.forEach((m, i) => {
    m.ordinal = i + 1;
  });
  return merged;
}

function normalizeStructuredNotebookSteps(steps) {
  if (!Array.isArray(steps)) return [];
  return steps.map((s) => ({
    ...s,
    system_events: [],
    actions: mergeStructuredActionsClient(s.actions || [])
  }));
}

function getProtocolStepDescription(stepNum, protocolSteps = []) {
  const target = parseInt(String(stepNum), 10);
  const match = (protocolSteps || []).find(
    (p) => p && parseInt(String(p.step_number), 10) === target
  );
  if (!match) return '';
  const text =
    (typeof match.description === 'string' && match.description) ||
    (typeof match.step_of_protocol === 'string' && match.step_of_protocol) ||
    '';
  return text.trim();
}

function resolveProtocolStepContent(stepNum, protocolSteps = [], fallback = '') {
  const fromDb = getProtocolStepDescription(stepNum, protocolSteps);
  if (fromDb) return fromDb;
  const fb = String(fallback || '').trim();
  if (fb && !/^step\s*\d+$/i.test(fb)) return fb;
  return '';
}

function formatStepHeaderLine(stepNum, protocolSteps = [], fallback = '') {
  const content = resolveProtocolStepContent(stepNum, protocolSteps, fallback);
  if (content) return `Step ${stepNum}: ${truncateText(content, 180)}`;
  return `Step ${stepNum}`;
}

/** Mirrors backend/utils/analyticsParser.js structured notebook (no S3 on client). */
function buildStructuredNotebookClient(detailedNotebook, protocolSteps, actionStepsByProtocolStep) {
  const protoDesc = {};
  (protocolSteps || []).forEach((p) => {
    if (!p || p.step_number === undefined) return;
    protoDesc[parseInt(String(p.step_number), 10)] = String(p.description || '').trim();
  });
  const s3Steps = {};
  if (actionStepsByProtocolStep && typeof actionStepsByProtocolStep === 'object') {
    Object.keys(actionStepsByProtocolStep).forEach((k) => {
      const n = parseInt(k, 10);
      if (!Number.isNaN(n)) s3Steps[n] = Array.isArray(actionStepsByProtocolStep[k]) ? actionStepsByProtocolStep[k] : [];
    });
  }

  function wallClockSec(line) {
    return parseTsFromLogLine(line);
  }
  function promptSub(firstLine) {
    const m = firstLine && firstLine.match(/PROMPT\[step\s*(\d+)\]/i);
    return m ? parseInt(m[1], 10) : null;
  }
  function titleFromPrompt(t) {
    const tm = t && t.match(/\bTITLE:\s*([^;\r\n]+)/i);
    return tm ? tm[1].trim() : '';
  }
  function quotedCur(t) {
    const m = t && t.match(/CURRENT ACTION STEP \(\d+\):\s*"([^"]+)"/);
    return m ? m[1].trim() : '';
  }
  function parseVerifier(lines) {
    const joined = (lines || []).join('\n');
    const idx = joined.indexOf('Raw response');
    if (idx < 0) return null;
    let rest = joined.slice(idx);
    const colon = rest.indexOf(':');
    if (colon < 0) return null;
    rest = rest.slice(colon + 1);
    const brace = rest.indexOf('{');
    if (brace < 0) return null;
    rest = rest.slice(brace).trim();
    try {
      return JSON.parse(rest);
    } catch {
      return null;
    }
  }

  return (detailedNotebook || []).map((stepEntry) => {
    const stepNum = stepEntry.step_number;
    const chunks = stepEntry.chunks || [];
    const protocol_description = protoDesc[stepNum] || '';
    const rawActions = [];
    let actionOrdinal = 0;
    chunks.forEach((ch) => {
      if (!ch || ch.kind !== 'cycle' || !ch.lines || !ch.lines.length) return;
      const lines = ch.lines;
      const joined = lines.join('\n');
      const firstLine = lines[0];
      const promptIdx = promptSub(firstLine);
      const s3Actions = s3Steps[stepNum] || [];
      const s3Entry = promptIdx !== null && s3Actions[promptIdx - 1] ? s3Actions[promptIdx - 1] : null;
      const label =
        (s3Entry && typeof s3Entry.title === 'string' && s3Entry.title.trim()) ||
        titleFromPrompt(joined) ||
        quotedCur(joined) ||
        (promptIdx !== null ? `Action step ${promptIdx}` : `Action ${actionOrdinal + 1}`);
      const step_context =
        s3Entry && typeof s3Entry.step_context === 'string' ? s3Entry.step_context.trim() : '';
      rawActions.push({
        ordinal: ++actionOrdinal,
        prompt_step_index: promptIdx,
        label,
        step_context,
        wall_time_sec: wallClockSec(firstLine),
        verdict: parseVerifier(lines),
        verbatim_log: joined
      });
    });
    return {
      step_number: stepNum,
      protocol_description,
      system_events: [],
      actions: mergeStructuredActionsClient(rawActions)
    };
  });
}

function getStructuredNotebookForDisplay(metrics, protocolStepsFromData) {
  if (Array.isArray(metrics.structured_notebook) && metrics.structured_notebook.length > 0) {
    return normalizeStructuredNotebookSteps(metrics.structured_notebook).map((step) => ({
      ...step,
      protocol_description:
        getProtocolStepDescription(step.step_number, protocolStepsFromData) ||
        (typeof step.protocol_description === 'string' ? step.protocol_description.trim() : '')
    }));
  }
  const dn = getDetailedNotebookForDisplay(metrics);
  if (!dn.length) return [];
  return normalizeStructuredNotebookSteps(
    buildStructuredNotebookClient(dn, protocolStepsFromData || [], {})
  );
}

function truncateText(s, max) {
  if (!s || typeof s !== 'string') return '';
  if (s.length <= max) return s;
  return s.slice(0, max) + '…';
}

function parseTsFromLogLine(line) {
  if (!line || typeof line !== 'string') return null;
  const m = line.match(/\[(\d{2}):(\d{2}):(\d{2})\]/);
  if (!m) return null;
  return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
}

function formatWallSecAsHms(sec) {
  return formatWallClockDisplay(sec);
}

/** Display wall-clock times in 12-hour AM/PM (frontend only). */
function formatWallClockDisplay(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const total = Math.floor(value);
    const h24 = Math.floor(total / 3600) % 24;
    const m = Math.floor((total % 3600) / 60);
    const s = Math.floor(total % 60);
    return formatHms12h(h24, m, s);
  }
  const str = String(value).trim();
  const m = str.match(/^(\d{1,2})[:.-](\d{2})[:.-](\d{2})$/);
  if (m) {
    return formatHms12h(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
  }
  return str;
}

function formatHms12h(h24, min, sec) {
  const h = Number(h24);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${period}`;
}

function extractSessionIdFromLogFilename(fileName) {
  if (!fileName || typeof fileName !== 'string') return null;
  const base = fileName.split('/').pop().trim();
  const match = base.match(/^(\d{14})_AILogs\.(txt|json|jsonl)$/i);
  return match ? match[1] : null;
}

function findBestArtifactForTimestamp(artifacts, targetSec, usedKeys = new Set(), maxDeltaSec = 120) {
  if (targetSec == null || !Number.isFinite(Number(targetSec))) return null;
  const target = Number(targetSec);
  let best = null;
  let bestDelta = Infinity;

  for (const artifact of artifacts || []) {
    if (!artifact || artifact.wall_time_sec == null) continue;
    // Deviation proofs are shown on deviation cards, not notebook timestamp matching.
    if (artifact.type === 'deviation_proof') continue;
    if (usedKeys.has(artifact.key)) continue;
    const delta = Math.abs(Number(artifact.wall_time_sec) - target);
    if (delta <= maxDeltaSec && delta < bestDelta) {
      bestDelta = delta;
      best = {
        ...artifact,
        match_delta_sec: delta,
        exact_match: delta === 0
      };
    }
  }

  return best;
}

function normalizeConfidencePct(confidence) {
  if (confidence == null || confidence === '') return null;
  const n = Number(confidence);
  if (!Number.isFinite(n)) return null;
  // Accept both 0–1 fractions and 0–100 percentages.
  if (n >= 0 && n <= 1) return Math.round(n * 100);
  if (n > 1 && n <= 100) return Math.round(n);
  return Math.round(Math.min(100, Math.max(0, n)));
}

function sanitizeProofUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const u = new URL(url.trim());
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.toString();
  } catch {
    return '';
  }
}

function stepPromptCount(step) {
  if (!step || typeof step !== 'object') return 0;
  const p = Number(step.prompts) || 0;
  const r = Number(step.responses) || 0;
  // Legacy txt counted each Gemini call as a prompt; older JSONL stored unique action_prompts only.
  return Math.max(p, r);
}

function resolveSessionDurationSec(summary, metrics) {
  const recorded = Number(summary?.total_time ?? metrics?.total_time ?? metrics?.session_timing?.duration_s);
  if (Number.isFinite(recorded) && recorded > 0) return recorded;
  const start = Number(metrics?.session_timing?.started_wall_sec ?? metrics?.global_start);
  const end = Number(metrics?.session_timing?.ended_wall_sec ?? metrics?.global_end);
  if (Number.isFinite(start) && Number.isFinite(end) && end >= start) return end - start;
  return Number.isFinite(recorded) && recorded >= 0 ? recorded : 0;
}

/** Wall-clock step duration for display (Unity step_start→step_end when available). */
function resolveStepDurationSec(step) {
  if (!step || typeof step !== 'object') return 0;
  if (step.boundary_duration != null && step.boundary_duration !== '') {
    const boundary = Number(step.boundary_duration);
    if (Number.isFinite(boundary) && boundary > 0) return boundary;
  }

  const direct = Number(step.time_to_complete ?? step.elapsed_time);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const bs = Number(step.boundary_start ?? step.first_prompt);
  const be = Number(step.boundary_end ?? step.completion);
  if (Number.isFinite(bs) && Number.isFinite(be) && be >= bs) return be - bs;

  const fp = Number(step.first_prompt);
  const lt = Number(step.last_timestamp);
  if (Number.isFinite(fp) && Number.isFinite(lt) && lt > fp) return lt - fp;

  if (Number.isFinite(fp) && step.completion != null) {
    const c = Number(step.completion);
    if (Number.isFinite(c) && c > fp) return c - fp;
  }
  return 0;
}

function resolveStepStartDisplay(step) {
  if (!step) return '';
  const sec = step.boundary_start ?? step.first_prompt;
  return formatWallClockDisplay(step.first_prompt_formatted || sec || null);
}

function resolveStepEndDisplay(step) {
  if (!step) return '';
  const sec = step.boundary_end ?? step.completion;
  if (sec == null && step.completion == null) return '';
  return formatWallClockDisplay(step.completion_formatted || sec || null);
}

function attachDeviationProofUrls(deviations, artifacts) {
  if (!Array.isArray(deviations) || !deviations.length) return deviations || [];
  const proofs = (artifacts || []).filter(
    (a) => a && (a.type === 'deviation_proof' || String(a.name || '').startsWith('deviationproof_'))
  );
  const used = new Set();

  return deviations.map((dev) => {
    if (!dev) return dev;
    const existingSafe = sanitizeProofUrl(dev.proof_url);
    if (existingSafe) return { ...dev, proof_url: existingSafe };

    const fileHint = (dev.proof_file || dev.frame_ref || '').split('/').pop();
    let match = null;

    if (fileHint) {
      match = proofs.find((a) => !used.has(a.key) && (a.name === fileHint || (a.key || '').endsWith(`/${fileHint}`)));
    }
    if (!match) {
      let target = null;
      if (typeof dev.time_sec === 'number' && Number.isFinite(dev.time_sec)) {
        target = dev.time_sec;
      } else if (typeof dev.time === 'number' && Number.isFinite(dev.time)) {
        target = dev.time;
      } else if (dev.time != null) {
        target = parseTsFromLogLine(`[${String(dev.time).trim()}]`);
      }
      if (target != null) {
        let bestDelta = Infinity;
        for (const a of proofs) {
          if (!a || a.wall_time_sec == null || used.has(a.key)) continue;
          const delta = Math.abs(Number(a.wall_time_sec) - Number(target));
          if (delta <= 5 && delta < bestDelta) {
            bestDelta = delta;
            match = a;
          }
        }
      }
    }

    if (match?.url) {
      const safeUrl = sanitizeProofUrl(match.url);
      if (safeUrl) {
        used.add(match.key);
        return {
          ...dev,
          proof_url: safeUrl,
          proof_file: match.name || fileHint || null,
          proof_s3_key: match.key || dev.proof_s3_key || null
        };
      }
    }
    // Drop unsafe / unmatched proof_url so UI never renders a bad src.
    if (dev.proof_url) {
      const { proof_url, ...rest } = dev;
      return rest;
    }
    return dev;
  });
}

function renderTraineeArtifactHtml(artifact, escapeFn) {
  if (!artifact || !artifact.url) return '';
  const typeLabels = {
    screenshot: 'Screenshot',
    videonote: 'Video clip',
    audionote: 'Audio note',
    deviation_proof: 'Deviation proof'
  };
  const typeIcons = {
    screenshot: 'camera-fill',
    videonote: 'camera-video-fill',
    audionote: 'mic-fill',
    deviation_proof: 'exclamation-triangle-fill'
  };
  const label = typeLabels[artifact.type] || 'Trainee media';
  const icon = typeIcons[artifact.type] || 'paperclip';
  const timeLabel = artifact.time_formatted ? escapeFn(formatWallClockDisplay(artifact.time_formatted)) : '';
  const matchNote = artifact.exact_match
    ? ''
    : `<span class="anl-artifact-match-note">closest match · ${artifact.match_delta_sec}s away</span>`;

  let media = '';
  if (artifact.type === 'screenshot' || artifact.type === 'deviation_proof') {
    media = `<img class="anl-artifact-img" src="${escapeFn(artifact.url)}" alt="${escapeFn(label)}" loading="lazy" />`;
  } else if (artifact.type === 'videonote') {
    media = `<video class="anl-artifact-video" src="${escapeFn(artifact.url)}" controls playsInline preload="metadata"></video>`;
  } else if (artifact.type === 'audionote') {
    media = `<audio class="anl-artifact-audio" src="${escapeFn(artifact.url)}" controls preload="metadata"></audio>`;
  }

  return `
    <div class="anl-artifact-media anl-artifact-media--${escapeFn(artifact.type || 'unknown')}">
      <div class="anl-artifact-label">
        <i class="bi bi-${icon}"></i>
        <span>${escapeFn(label)}</span>
        ${timeLabel ? `<span class="anl-artifact-time">${timeLabel}</span>` : ''}
        ${matchNote}
      </div>
      ${media}
    </div>`;
}

function renderTraineeArtifactsForTimestamp(artifacts, targetSec, usedKeys, escapeFn) {
  const matched = findBestArtifactForTimestamp(artifacts, targetSec, usedKeys);
  if (!matched) return '';
  usedKeys.add(matched.key);
  return renderTraineeArtifactHtml(matched, escapeFn);
}

/* ─── Parse raw verbatim log text into structured entries ─── */
function parseVerbatimLogEntries(logText) {
  if (!logText || typeof logText !== 'string') return [];
  const entries = [];
  // Split on the ──── separator that mergeStructuredActionsClient inserts between cycles
  const sections = logText.split(/\n?─{4,}\n?/);
  sections.forEach((section) => {
    if (!section.trim()) return;
    const rawIdx = section.indexOf('Raw response:');
    if (rawIdx === -1) return;
    // Grab timestamp from the "Raw response" line
    const rawLineMatch = section.match(/\[(\d{2}:\d{2}:\d{2})\][^\n]*Raw response/);
    const timestamp = rawLineMatch ? rawLineMatch[1] : null;
    const afterRaw = section.slice(rawIdx + 'Raw response:'.length).trim();
    const braceStart = afterRaw.indexOf('{');
    if (braceStart === -1) return;
    try {
      let depth = 0, end = -1;
      for (let i = braceStart; i < afterRaw.length; i++) {
        if (afterRaw[i] === '{') depth++;
        else if (afterRaw[i] === '}') { depth--; if (depth === 0) { end = i + 1; break; } }
      }
      if (end === -1) return;
      const parsed = JSON.parse(afterRaw.slice(braceStart, end));
      entries.push({
        timestamp,
        completed:        parsed.completed === true,
        wrong_action:     parsed.wrong_action === true,
        wrong_reason:     typeof parsed.wrong_reason === 'string' ? parsed.wrong_reason.trim() : '',
        evidence:         typeof parsed.evidence === 'string' ? parsed.evidence.trim() : '',
        confidence:       typeof parsed.confidence === 'number' ? parsed.confidence : null,
        objects_detected: Array.isArray(parsed.objects_detected) ? parsed.objects_detected : [],
      });
    } catch (e) { /* skip malformed JSON */ }
  });
  return entries;
}

function renderVerbatimLogEntries(entries, escapeFn, artifacts = [], usedArtifactKeys = null) {
  if (!entries || !entries.length) return '';
  const usedKeys = usedArtifactKeys || new Set();
  let h = '<div class="anl-log-entries">';
  entries.forEach((e) => {
    let icon, cls, label;
    if (e.completed) {
      icon  = '<i class="bi bi-check-circle-fill" style="color:#27ae60;"></i>';
      cls   = 'anl-log-entry--ok';
      label = 'Completed';
    } else if (e.wrong_action) {
      icon  = '<i class="bi bi-x-circle-fill" style="color:#e74c3c;"></i>';
      cls   = 'anl-log-entry--bad';
      label = 'Deviation';
    } else {
      icon  = '<i class="bi bi-dash-circle" style="color:#94a3b8;"></i>';
      cls   = 'anl-log-entry--neutral';
      label = 'Checking…';
    }
    const confPct = e.confidence !== null ? normalizeConfidencePct(e.confidence) : null;
    h += `<div class="anl-log-entry ${cls}">`;
    h += `<div class="anl-log-entry-header">`;
    h += `<span class="anl-log-entry-status">${icon} <span>${label}</span></span>`;
    if (e.timestamp) h += `<span class="anl-log-entry-ts"><i class="bi bi-clock"></i> ${escapeFn(formatWallClockDisplay(e.timestamp))}</span>`;
    if (confPct !== null) h += `<span class="anl-log-entry-conf">Confidence: ${confPct}%</span>`;
    h += '</div>';
    if (e.evidence)         h += `<div class="anl-log-entry-row"><strong>Evidence:</strong> ${escapeFn(e.evidence)}</div>`;
    if (e.wrong_reason)     h += `<div class="anl-log-entry-row anl-log-entry-row--bad"><strong>Wrong reason:</strong> ${escapeFn(e.wrong_reason)}</div>`;
    if (e.objects_detected.length) h += `<div class="anl-log-entry-row anl-log-entry-row--objects"><strong>Objects:</strong> ${escapeFn(e.objects_detected.join(', '))}</div>`;
    if (e.timestamp && artifacts.length) {
      const targetSec = parseTsFromLogLine(`[${e.timestamp}]`);
      h += renderTraineeArtifactsForTimestamp(artifacts, targetSec, usedKeys, escapeFn);
    }
    h += '</div>';
  });
  h += '</div>';
  return h;
}

function devTimeToSeconds(dev) {
  if (dev == null) return null;
  if (typeof dev.time_sec === 'number' && !Number.isNaN(dev.time_sec)) return dev.time_sec;
  if (typeof dev.time === 'number' && !Number.isNaN(dev.time)) return dev.time;
  const s = dev.time != null ? String(dev.time).trim() : '';
  const m = s.match(/^(\d{2}):(\d{2}):(\d{2})/);
  if (m) return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
  return null;
}

function renderNotebookSummaryAsParagraphs(text, escapeHtml) {
  if (!text || typeof text !== 'string') return '';

  const paragraphs = [];
  let buffer = [];

  const normalizeLine = (line) =>
    line.trim().replace(/^[•\-\*]\s+/, '').replace(/^\d+[\.\)]\s+/, '');

  const flush = () => {
    if (buffer.length) {
      paragraphs.push(buffer.join(' '));
      buffer = [];
    }
  };

  text.split('\n').forEach((line) => {
    const normalized = normalizeLine(line);
    if (!normalized) flush();
    else buffer.push(normalized);
  });
  flush();

  if (!paragraphs.length) return '';

  return `<div class="anl-nb-summary-prose">${paragraphs
    .map((p) => `<p class="anl-nb-para">${escapeHtml(p)}</p>`)
    .join('')}</div>`;
}

function buildProseFromTranscriptionItems(items, escapeHtml, maxItems = 150) {
  if (!items || !items.length) return '';

  const sentences = [];
  items.slice(0, maxItems).forEach((trans) => {
    const actionText = (trans.action || trans.text || '').trim();
    if (!actionText) return;

    const timeStr = formatWallClockDisplay(trans.time || trans.time_formatted || '');
    const stepNum = trans.step != null && trans.step !== '' ? String(trans.step) : '';
    const isCorrect = trans.is_correct !== false;
    const wrongReason = (trans.wrong_reason || '').trim();

    let sentence = '';
    if (stepNum) sentence += `In step ${stepNum}, `;
    if (timeStr) sentence += `at ${timeStr}, `;
    sentence += actionText.replace(/\.$/, '');
    if (!isCorrect && wrongReason) {
      sentence += ` (deviation: ${wrongReason})`;
    }
    sentence += '.';
    sentences.push(sentence);
  });

  if (!sentences.length) return '';

  let prose = sentences.join(' ');
  if (items.length > maxItems) {
    prose += ` (${items.length - maxItems} additional recorded actions omitted.)`;
  }

  return renderNotebookSummaryAsParagraphs(prose, escapeHtml);
}

function buildNarrativeSummaryFromStructuredNotebook(structuredSb, escapeHtml) {
  if (!Array.isArray(structuredSb) || !structuredSb.length) return '';

  const paragraphs = [];
  structuredSb.forEach((step) => {
    const actions = step.actions || [];
    if (!actions.length) return;

    let opener = `In step ${step.step_number}`;
    if (step.protocol_description) {
      opener += ` (${step.protocol_description.replace(/\.$/, '')})`;
    }
    opener += ', the trainee ';

    const parts = actions.map((act, idx) => {
      const label = (act.label || `action ${idx + 1}`).replace(/\.$/, '');
      const timeStr = act.wall_time_sec != null ? formatWallClockDisplay(act.wall_time_sec) : '';
      const v = act.verdict;
      let part = '';

      if (timeStr) part += `at ${timeStr} `;
      part += `performed "${label}"`;

      if (v) {
        if (v.wrong_action === true) {
          part += ' incorrectly';
          if (v.wrong_reason) part += `: ${v.wrong_reason.replace(/\.$/, '')}`;
          else if (v.evidence) part += ` — ${v.evidence.replace(/\.$/, '')}`;
        } else if (v.completed === true) {
          part += ' correctly';
          if (v.evidence) part += ` — ${v.evidence.replace(/\.$/, '')}`;
        } else {
          part += ' but did not complete the expected action';
          if (v.evidence) part += ` — ${v.evidence.replace(/\.$/, '')}`;
        }
      }

      return part;
    });

    paragraphs.push(`${opener}${parts.join('; then ')}.`);
  });

  if (!paragraphs.length) return '';
  return renderNotebookSummaryAsParagraphs(paragraphs.join('\n\n'), escapeHtml);
}

function collectTranscriptionFromStepStats(stepStats) {
  const items = [];
  const stepKeys = Object.keys(stepStats || {}).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  stepKeys.forEach((stepNum) => {
    const step = stepStats[stepNum];
    if (step.transcription && step.transcription.length > 0) {
      step.transcription.forEach((t) => {
        if (t && t.action) {
          items.push({
            ...t,
            step: stepNum,
            time: formatWallClockDisplay(t.time_formatted || (t.time !== undefined ? t.time : ''))
          });
        }
      });
    } else if (step.done_actions && step.done_actions.length > 0) {
      step.done_actions.forEach((a) => {
        if (a) items.push({ action: a, is_correct: true, step: stepNum });
      });
    }
  });
  return items;
}

function renderOriginalDeviationCard(dev, idx, escapeHtml, options = {}) {
  const minimal = options && options.minimal === true;
  const devDesc = escapeHtml(dev.description || dev.reason || 'Deviation detected');
  const devImpact = dev.impact ? escapeHtml(dev.impact) : '';
  const devPrev = dev.prevention ? escapeHtml(dev.prevention) : '';
  const proofUrl = sanitizeProofUrl(dev.proof_url || '');
  const escapedProof = proofUrl ? escapeHtml(proofUrl) : '';
  const devTimeStr =
    dev.time_formatted != null && dev.time_formatted !== ''
      ? escapeHtml(formatWallClockDisplay(dev.time_formatted))
      : dev.time != null && dev.time !== ''
        ? escapeHtml(formatWallClockDisplay(dev.time))
        : '';
  const devConf = normalizeConfidencePct(dev.confidence);
  const stepLabel = dev.step != null && dev.step !== '' ? escapeHtml(String(dev.step)) : 'N/A';

  const proofHtml = escapedProof
    ? `<div class="anl-dev-proof">
         <div class="anl-dev-proof-toolbar">
           <span class="anl-dev-proof-label"><i class="bi bi-camera-fill"></i> Deviation proof</span>
           ${SHOW_PROOF_ENLARGE_BUTTON ? `<button type="button" class="anl-dev-proof-expand" data-proof-url="${escapedProof}" title="Enlarge proof image">
             <i class="bi bi-arrows-fullscreen"></i> Enlarge
           </button>` : ''}
         </div>
         <button type="button" class="anl-dev-proof-thumb" data-proof-url="${escapedProof}" title="Click to enlarge">
           <img class="anl-dev-proof-img" src="${escapedProof}" alt="Deviation proof frame" loading="lazy" />
         </button>
       </div>`
    : '';

  const fieldsHtml = `
            <div class="anl-dev-fields">
              <div class="anl-dev-field">
                <div class="anl-dev-field-lbl">Deviation</div>
                <div class="anl-dev-field-val">${devDesc}</div>
              </div>
              ${devImpact ? `<div class="anl-dev-field">
                <div class="anl-dev-field-lbl">Impact</div>
                <div class="anl-dev-field-val">${devImpact}</div>
              </div>` : ''}
              ${devPrev ? `<div class="anl-dev-field">
                <div class="anl-dev-field-lbl">Prevention</div>
                <div class="anl-dev-field-val">${devPrev}</div>
              </div>` : ''}
            </div>`;

  if (minimal) {
    return `
          <div class="anl-dev-card-min">
            <div class="anl-dev-card-head">
              <span class="anl-dev-num-badge">${idx}</span>
              ${devTimeStr ? `<span class="anl-dev-time-badge"><i class="bi bi-clock"></i> ${devTimeStr}</span>` : ''}
              ${devConf !== null ? `<span class="anl-dev-conf-badge">${devConf}%</span>` : ''}
            </div>
            <div class="anl-dev-card-body anl-dev-card-body--split${escapedProof ? ' anl-dev-card-body--with-proof' : ''}">
              <div class="anl-dev-card-copy">
                ${fieldsHtml}
              </div>
              ${proofHtml ? `<div class="anl-dev-card-proof-col">${proofHtml}</div>` : ''}
            </div>
          </div>`;
  }

  return `
          <div class="anl-dev-card-full">
            <div class="anl-dev-card-head anl-dev-card-head--full">
              <span class="anl-dev-num-badge">${idx}</span>
              <span class="anl-dev-step-chip">Step ${stepLabel}</span>
              ${devTimeStr ? `<span class="anl-dev-time-badge"><i class="bi bi-clock"></i> ${devTimeStr}</span>` : ''}
              ${devConf !== null ? `<span class="anl-dev-conf-badge">Confidence ${devConf}%</span>` : ''}
            </div>
            <div class="anl-dev-card-body anl-dev-card-body--split${escapedProof ? ' anl-dev-card-body--with-proof' : ''}">
              <div class="anl-dev-card-copy">
                ${fieldsHtml}
              </div>
              ${proofHtml ? `<div class="anl-dev-card-proof-col">${proofHtml}</div>` : ''}
            </div>
          </div>`;
}

function renderDeviationStepwiseHtml(allDeviations, structuredNb, detailedNbFallback, protocolSteps, escapeHtml) {
  if (!allDeviations || !allDeviations.length) return '';

  let html = '<div class="anl-nb-accordion anl-nb-accordion--dev">';

  const byStep = new Map();
  allDeviations.forEach((dev) => {
    const raw = dev.step;
    const sn = raw != null && raw !== 'N/A' ? Number(raw) : NaN;
    const key = Number.isFinite(sn) ? sn : -1;
    if (!byStep.has(key)) byStep.set(key, []);
    byStep.get(key).push(dev);
  });

  const fromStruct = (structuredNb || []).map((d) => d.step_number);
  const fromLegacy = (detailedNbFallback || []).map((d) => d.step_number);
  const fromDev = [...byStep.keys()].filter((k) => k >= 0);
  const stepsOrder = [...new Set([...fromStruct, ...fromLegacy, ...fromDev])].sort((a, b) => a - b);

  let globalDeviationIndex = 0;

  for (const stepNum of stepsOrder) {
    const devsForStep = byStep.get(stepNum) || [];
    if (!devsForStep.length) continue;

    const structStep = (structuredNb || []).find((s) => s.step_number === stepNum);
    const legacyEntry = (detailedNbFallback || []).find((s) => s.step_number === stepNum);
    const legacyChunks = legacyEntry ? legacyEntry.chunks || [] : [];
    const legacyCycles = legacyChunks.filter((c) => c.kind === 'cycle');

    const stepContent = resolveProtocolStepContent(
      stepNum,
      protocolSteps,
      (structStep && structStep.protocol_description) ||
        (structStep && structStep.actions && structStep.actions[0] && structStep.actions[0].step_context) ||
        ''
    );

    html += `<details class="anl-dev-accordion-step anl-nb-step" open>`;
    html += `<summary class="anl-nb-summary anl-nb-summary--dev-step">`;
    html += `<span class="anl-nb-summary-main anl-dev-step-main">`;
    html += `<span class="anl-nb-step-num anl-dev-step-chip">Step ${escapeHtml(String(stepNum))}</span>`;
    html += `<span class="anl-nb-badge anl-nb-badge--dev">${devsForStep.length} Deviation${devsForStep.length > 1 ? 's' : ''}</span>`;
    html += stepContent
      ? `<span class="anl-dev-step-content">: ${escapeHtml(truncateText(stepContent, 180))}</span>`
      : '';
    html += `</span>`;
    html += `<span class="anl-nb-summary-right">`;
    html += `<span class="anl-dev-toggle-hint">Details</span>`;
    html += `<span class="anl-nb-chevron" aria-hidden="true"><i class="bi bi-chevron-down"></i></span>`;
    html += `</span></summary>`;
    html += `<div class="anl-nb-body anl-nb-body--dev-step">`;

    const used = new Set();
    const actions = structStep && Array.isArray(structStep.actions) && structStep.actions.length
      ? structStep.actions
      : null;

    const renderDevList = (devs) => {
      let block = '<div class="anl-dev-stack">';
      devs.forEach((d) => {
        globalDeviationIndex += 1;
        block += renderOriginalDeviationCard(d, globalDeviationIndex, escapeHtml, { minimal: true });
      });
      block += '</div>';
      return block;
    };

    if (actions && actions.length) {
      actions.forEach((act, ai) => {
        const next = actions[ai + 1];
        const t0 = act.wall_time_sec;
        const t1 =
          next && next.wall_time_sec != null && next.wall_time_sec >= (t0 != null ? t0 : -1)
            ? next.wall_time_sec
            : 86400 * 2;

        const devsHere = devsForStep.filter((d, di) => {
          if (used.has(di)) return false;
          const ts = devTimeToSeconds(d);
          if (ts == null || t0 == null) return false;
          if (ts >= t0 && ts < t1) {
            used.add(di);
            return true;
          }
          return false;
        });
        if (!devsHere.length) return;

        const num =
          act.prompt_step_index != null ? Number(act.prompt_step_index) : act.ordinal || ai + 1;
        const actionLine = `Action ${num} · ${act.label || 'Action'}`;
        html += `<div class="anl-dev-action-block">`;
        html += `<div class="anl-dev-action-label">${escapeHtml(actionLine)}</div>`;
        html += renderDevList(devsHere);
        html += `</div>`;
      });
    } else if (legacyCycles.length) {
      legacyCycles.forEach((c, ci) => {
        const lines = c.lines || [];
        const sumLine = lines[0] || `Action ${ci + 1}`;
        const t0 = parseTsFromLogLine(lines[0] || '');
        const next = legacyCycles[ci + 1];
        const t1Raw = next && next.lines && next.lines[0] ? parseTsFromLogLine(next.lines[0]) : null;
        const t1 = t1Raw != null ? t1Raw : 86400 * 2;

        const devsHere = devsForStep.filter((d, di) => {
          if (used.has(di)) return false;
          const ts = devTimeToSeconds(d);
          if (ts == null || t0 == null) return false;
          if (ts >= t0 && ts < t1) {
            used.add(di);
            return true;
          }
          return false;
        });
        if (!devsHere.length) return;

        html += `<div class="anl-dev-action-block">`;
        html += `<div class="anl-dev-action-label">${escapeHtml(truncateText(sumLine, 80))}</div>`;
        html += renderDevList(devsHere);
        html += `</div>`;
      });
    } else {
      html += renderDevList(devsForStep);
      devsForStep.forEach((_, di) => used.add(di));
    }

    const orphan = devsForStep.filter((d, di) => !used.has(di));
    if (orphan.length) {
      html += `<div class="anl-dev-action-block">`;
      if (used.size > 0) html += `<div class="anl-dev-action-label">Other timing</div>`;
      html += renderDevList(orphan);
      html += `</div>`;
    }

    html += `</div></details>`;
  }

  if (byStep.has(-1) && (byStep.get(-1) || []).length) {
    const na = byStep.get(-1) || [];
    html += `<details class="anl-dev-accordion-step anl-nb-step" open>`;
    html += `<summary class="anl-nb-summary anl-nb-summary--dev-step">`;
    html += `<span class="anl-nb-summary-main anl-dev-step-main">`;
    html += `<span class="anl-nb-step-num anl-dev-step-chip">Unassigned</span>`;
    html += `<span class="anl-nb-badge anl-nb-badge--dev">${na.length} Deviation${na.length > 1 ? 's' : ''}</span>`;
    html += `</span>`;
    html += `<span class="anl-nb-summary-right"><span class="anl-nb-chevron" aria-hidden="true"><i class="bi bi-chevron-down"></i></span></span>`;
    html += `</summary>`;
    html += `<div class="anl-nb-body anl-nb-body--dev-step"><div class="anl-dev-stack">`;
    na.forEach((d) => {
      globalDeviationIndex += 1;
      html += renderOriginalDeviationCard(d, globalDeviationIndex, escapeHtml, { minimal: true });
    });
    html += `</div></div></details>`;
  }

  html += '</div>';
  html += `<div class="anl-dev-tip"><i class="bi bi-lightbulb"></i> Tip: Expand a step to review deviation, impact, prevention, and proof frames. Click any proof image to view it larger.</div>`;
  return html;
}

/* ─── Main Component ─── */
const Analytics = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scheduled');
  const [projects, setProjects] = useState([]);
  const [selfConsumedFiles, setSelfConsumedFiles] = useState([]);
  const [personalStats, setPersonalStats] = useState({
    total_projects: 0,
    completed: 0,
    scheduled: 0,
    in_progress: 0,
    published: 0,
    org_users: 0
  });
  const [orgStats, setOrgStats] = useState({
    total_users: 0,
    completed_experiments: 0,
    org_name: 'Organization'
  });
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);
  const [chatContext, setChatContext] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [autoLoadScheduleId, setAutoLoadScheduleId] = useState(null);
  const [processingProgress, setProcessingProgress] = useState({
    phase: '', percentage: 0, message: '', details: ''
  });
  const [analyticsHtml, setAnalyticsHtml]         = useState('');
  const [analyticsError, setAnalyticsError]       = useState('');
  const [showRightPanel, setShowRightPanel]       = useState(false);
  const [modalProtocolName, setModalProtocolName] = useState('Experiment Analysis');
  const [uploadStatus, setUploadStatus]           = useState('');
  const [uploadLoading, setUploadLoading]         = useState(false);
  const [exportPdfLoading, setExportPdfLoading]   = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    enotebook: true,
    enotebook_detail: true,
    deviations: true,
    stepPerformance: true
  });

  useEffect(() => {
    fetchAnalyticsData();
    const scheduleIdParam = searchParams.get('schedule_id');
    if (scheduleIdParam) {
      setAutoLoadScheduleId(scheduleIdParam);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('schedule_id');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (autoLoadScheduleId && !loading) {
      loadScheduleAnalytics(autoLoadScheduleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadScheduleId, loading]);
  
  const loadScheduleAnalytics = async (scheduleId) => {
    try {
      const scheduleRes = await API.get(`/qurator/calendar/schedule-details/${scheduleId}`);
      if (scheduleRes.data.success && scheduleRes.data.schedule) {
        const schedule = scheduleRes.data.schedule;
        const filename = schedule.filename ? schedule.filename.trim().replace(/\+$/, '').trim() : null;
        if (!filename) {
          setShowAnalyzeModal(true);
          setAnalyzeLoading(false);
          return;
        }
        await handleAnalyze(
          scheduleId,
          schedule.experiment_id,
          filename,
          schedule.project_id,
          false,
          false,
          schedule.protocol_name
        );
      }
    } catch (error) {
      console.error('Error loading schedule analytics:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const [statsRes, projectsRes, selfConsumedRes] = await Promise.all([
        API.get('/qurator/analytics/stats'),
        API.get('/qurator/analytics/projects'),
        API.get('/qurator/analytics/self-consumed')
      ]);
      if (statsRes.data.success) {
        setPersonalStats(statsRes.data.personal_stats);
        setOrgStats(statsRes.data.org_stats);
      }
      if (projectsRes.data.success) setProjects(projectsRes.data.projects);
      if (selfConsumedRes.data.success) {
        const jsonlFiles = (selfConsumedRes.data.files || []).filter(
          (file) => typeof file?.filename === 'string' && /_AILogs\.jsonl$/i.test(file.filename)
        );
        setSelfConsumedFiles(jsonlFiles);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const resolveExperimentDisplayName = (data, hintName) => {
    const schedule = data?.schedule || {};
    const candidates = [
      hintName,
      schedule.protocol_name,
      schedule.experiment_name,
      schedule.draft_title,
      data?.protocol_name,
      data?.session?.protocol_name
    ];
    for (const candidate of candidates) {
      const name = candidate != null ? String(candidate).trim() : '';
      if (name && name !== 'Experiment' && name !== 'Unknown' && name !== 'Unknown Protocol') {
        return name;
      }
    }
    for (const candidate of candidates) {
      const name = candidate != null ? String(candidate).trim() : '';
      if (name) return name;
    }
    return 'Experiment Analysis';
  };

  const fetchLogFromS3 = async (experimentId, filename) => {
    // Prefer JSONL when a legacy txt name is provided
    const candidates = [];
    if (filename) {
      candidates.push(filename);
      if (/_AILogs\.txt$/i.test(filename)) {
        candidates.unshift(filename.replace(/_AILogs\.txt$/i, '_AILogs.jsonl'));
      }
      if (/_AILogs\.json$/i.test(filename)) {
        candidates.unshift(filename.replace(/_AILogs\.json$/i, '_AILogs.jsonl'));
      }
    }
    let lastError = null;
    for (const name of candidates) {
      try {
        const response = await API.get('/qurator/analytics/fetch-log', {
          params: { experiment_id: experimentId, filename: name }
        });
        if (response.data.ok) return response.data.content;
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error('Failed to fetch log file');
  };

  const fetchTraineeArtifacts = async (experimentId, logFilename) => {
    if (!experimentId) return [];
    const sessionId = extractSessionIdFromLogFilename(logFilename);
    try {
      const response = await API.get('/qurator/analytics/trainee-artifacts', {
        params: {
          experiment_id: experimentId,
          ...(sessionId ? { session_id: sessionId } : {})
        }
      });
      if (response.data?.ok) return response.data.artifacts || [];
    } catch (error) {
      console.warn('Failed to fetch trainee artifacts:', error);
    }
    return [];
  };

  const attachArtifactsAndDisplay = async (analyticsData, experimentId, logFilename, protocolNameHint = null) => {
    const resolvedLogFilename =
      logFilename ||
      analyticsData?.schedule?.filename ||
      (analyticsData?.session_id ? `${analyticsData.session_id}_AILogs.jsonl` : null);
    // Prefer live signed URLs from S3 listing; meta from run tables is timing-only
    const artifacts = await fetchTraineeArtifacts(experimentId, resolvedLogFilename);
    displayAnalytics({
      ...analyticsData,
      trainee_artifacts: artifacts,
      from_run_tables: !!(analyticsData?.from_run_tables || analyticsData?.from_jsonl)
    }, protocolNameHint);
  };

  const handleAnalyze = async (
    scheduleId,
    experimentId,
    filename,
    projectId = null,
    isSelfConsumed = false,
    forceReprocess = false,
    protocolName = null
  ) => {
    try {
      setAnalyzeLoading(true);
      setShowAnalyzeModal(true);
      setCurrentAnalysis({ scheduleId, experimentId, filename, projectId, isSelfConsumed, protocolName });
      setChatContext([]);
      if (!forceReprocess) {
        setCurrentAnalyticsData(null);
        setAnalyticsHtml('');
      }
      setAnalyticsError('');
      if (!forceReprocess) setShowRightPanel(false);
      setModalProtocolName(protocolName || 'Experiment Analysis');
      
      // Check for cached analytics (skipped when reprocessing)
      if (!forceReprocess) {
        setProcessingProgress({ phase: 'checking', percentage: 5, message: 'Checking for cached analytics...', details: '' });
        try {
          const checkRes = await API.post('/qurator/analytics/check-existing', {
            schedule_id: isSelfConsumed ? null : scheduleId,
            filename: isSelfConsumed ? filename : null,
            experiment_id: isSelfConsumed ? experimentId : null
          });
          if (checkRes.data.ok && checkRes.data.exists && checkRes.data.analytics) {
            const fromTables = checkRes.data.from_run_tables || checkRes.data.analytics.from_run_tables;
            setProcessingProgress({
              phase: 'complete',
              percentage: 100,
              message: fromTables ? 'Loading analytics from run tables...' : 'Loading cached analytics...',
              details: ''
            });
            await attachArtifactsAndDisplay(checkRes.data.analytics, experimentId, filename, protocolName);
            setAnalyzeLoading(false);
            return;
          }
        } catch (e) { /* continue */ }
      } else {
        setProcessingProgress({
          phase: 'fetching',
          percentage: 8,
          message: 'Reprocessing — refreshing analytics from log...',
          details: 'Previous cached results will be replaced'
        });
      }
      
      // Fetch log
      setProcessingProgress({
        phase: 'fetching',
        percentage: forceReprocess ? 12 : 10,
        message: forceReprocess ? 'Fetching log for reprocess...' : 'Fetching log file from server...',
        details: ''
      });
      const logContent = await fetchLogFromS3(experimentId, filename);

      const looksLikeJsonl = /^\s*\{/.test(logContent) && logContent.includes('"t":');
      setProcessingProgress({
        phase: 'parsing',
        percentage: 20,
        message: looksLikeJsonl
          ? 'Ingesting JSONL run into analytics tables...'
          : 'Parsing log file and identifying steps...',
        details: `Processing ${logContent.length.toLocaleString()} characters`
      });
      
      const startTime = Date.now();
      let progressPhase = 0;
      const progressInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        progressPhase++;
        let percentage = 20, message = looksLikeJsonl ? 'Writing run tables...' : 'Parsing log file...';
        if (looksLikeJsonl) {
          percentage = Math.min(90, 20 + elapsed * 8);
          message = elapsed > 3 ? 'Building e-notebook from JSONL...' : 'Writing run tables...';
        } else if (elapsed > 5  && elapsed <= 20)  { percentage = Math.min(45, 20 + elapsed * 1.5); message = 'Analyzing experiment steps...'; }
        else if (elapsed > 20 && elapsed <= 50) { percentage = Math.min(70, 45 + (elapsed - 20)); message = 'Running AI analysis in parallel...'; }
        else if (elapsed > 50 && elapsed <= 90) { percentage = Math.min(85, 70 + Math.floor((elapsed - 50) / 3)); message = 'Generating E-notebook and insights...'; }
        else if (elapsed > 90) { percentage = Math.min(93, 85 + Math.floor((elapsed - 90) / 15)); message = 'Finalizing analysis...'; }
        setProcessingProgress({ phase: 'analyzing', percentage, message, details: `${elapsed}s elapsed` });
      }, 1000);
      
      let processRes;
      try {
        const requestTimeout = setTimeout(() => { clearInterval(progressInterval); }, 300000);
        if (isSelfConsumed) {
          processRes = await API.post('/qurator/analytics/process-self-consumed', { experiment_id: experimentId, log_text: logContent, filename });
        } else {
          processRes = await API.post('/qurator/analytics/process-experiment', { schedule_id: scheduleId, log_text: logContent });
        }
        clearTimeout(requestTimeout);
      } finally {
        clearInterval(progressInterval);
      }
      
      if (processRes.data.ok) {
        setProcessingProgress({ phase: 'complete', percentage: 100, message: 'Analysis complete!', details: '' });
        setAnalyzeLoading(false);
        await attachArtifactsAndDisplay(processRes.data, experimentId, filename, protocolName);
        setTimeout(() => setProcessingProgress({ phase: '', percentage: 0, message: '', details: '' }), 2000);
      } else {
        throw new Error(processRes.data.error || 'Failed to process experiment');
      }
    } catch (error) {
      console.error('Error analyzing experiment:', error);
      setAnalyticsError(error.message || 'Failed to analyze experiment');
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleReprocess = () => {
    if (!currentAnalysis || analyzeLoading) return;
    const { scheduleId, experimentId, filename, projectId, isSelfConsumed, protocolName } = currentAnalysis;
    handleAnalyze(scheduleId, experimentId, filename, projectId, isSelfConsumed, true, protocolName);
  };

  const handleExportPdf = async () => {
    if (!currentAnalyticsData || exportPdfLoading) return;
    setExportPdfLoading(true);
    try {
      let traineeName = currentAnalyticsData.schedule?.username || '';
      let projectName = currentAnalysis?.projectName || '';
      if (currentAnalysis?.scheduleId) {
        for (const project of projects) {
          const exp = project.experiments?.find((e) => e.schedule_id === currentAnalysis.scheduleId);
          if (exp) {
            traineeName = exp.username || `${exp.first_name || ''} ${exp.last_name || ''}`.trim();
            projectName = project.project_name;
            break;
          }
        }
      }
      await downloadAnalyticsPdf({
        apiPath: '/qurator/analytics/export-pdf',
        analytics: currentAnalyticsData,
        account: {
          report_by: user?.username || user?.first_name || 'User',
          email: user?.email || '',
          org_name: orgStats.org_name,
          trainee_name: traineeName,
          project_name: projectName,
          protocol_name: modalProtocolName,
        },
        brand: 'qurator',
        filename: analyticsPdfFilename(modalProtocolName),
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      window.alert('Could not generate the analytics PDF. Please try again.');
    } finally {
      setExportPdfLoading(false);
    }
  };

  const displayAnalytics = (data, protocolNameHint = null) => {
    setCurrentAnalyticsData(data);
    
    const schedule = data.schedule || {};
    const metrics = data.metrics || {};
    const reproducibility = data.reproducibility || {};
    const gemini = data.gemini || {};
    let summary = metrics.summary || {};
    
    if (!summary.total_steps && metrics.steps) summary.total_steps = metrics.steps.length || 0;
    if (summary.completed_steps === undefined && metrics.step_stats)
      summary.completed_steps = Object.values(metrics.step_stats).filter(
        (s) => s.is_completed === true
      ).length;
    if (!summary.total_time && metrics.total_time) summary.total_time = metrics.total_time;
    if (summary.actual_deviations === undefined && metrics.actual_deviations !== undefined)
      summary.actual_deviations = metrics.actual_deviations;
    if (summary.avg_confidence === undefined) {
      const conf = metrics.avg_confidence || 0;
      summary.avg_confidence = conf > 1 ? conf / 100 : conf;
    }
    
    setModalProtocolName(
      resolveExperimentDisplayName(data, protocolNameHint || currentAnalysis?.protocolName)
    );

    const finalReproScore  = reproducibility.total_score || reproducibility.score || 0;
    const finalGrade       = reproducibility.grade || 'F';
    const finalTotalTime   = resolveSessionDurationSec(summary, metrics);
    const finalCompleted   = summary.completed_steps !== undefined ? summary.completed_steps : 0;
    const finalTotal       = summary.total_steps || metrics.steps?.length || 0;
    const finalDeviations  = summary.actual_deviations !== undefined ? summary.actual_deviations : (metrics.actual_deviations || 0);
    const finalConfidence  = summary.avg_confidence !== undefined ? summary.avg_confidence : (metrics.avg_confidence || 0);
    
    const detailedNb = getDetailedNotebookForDisplay(metrics);
    const protocolSteps = data.protocol_steps || [];
    const structuredSb = getStructuredNotebookForDisplay(metrics, protocolSteps);
    const traineeArtifacts = data.trainee_artifacts || [];
    const usedArtifactKeys = new Set();
    let detailedSectionHtml = '';
    const detOpen = !collapsedSections.enotebook_detail;
    let dnInner = '';

    if (structuredSb.length > 0) {
      dnInner += '<div class="anl-nb-accordion anl-nb-accordion--detail">';
      structuredSb.forEach((step) => {
        const sn = step.step_number;
        const stepContent = resolveProtocolStepContent(sn, protocolSteps, step.protocol_description);
        const actionCount = (step.actions || []).length;
        dnInner += `<details class="anl-verbatim-step anl-nb-step anl-nb-step--detail">`;
        dnInner += `<summary class="anl-nb-summary anl-nb-summary--detail-step">`;
        dnInner += `<span class="anl-nb-summary-main anl-dev-step-main">`;
        dnInner += `<span class="anl-nb-step-num anl-detail-step-chip">Step ${escapeHtml(String(sn))}</span>`;
        if (actionCount) {
          dnInner += `<span class="anl-nb-badge anl-nb-badge--actions">${actionCount} action${actionCount > 1 ? 's' : ''}</span>`;
        }
        dnInner += stepContent
          ? `<span class="anl-nb-step-content">: ${escapeHtml(truncateText(stepContent, 180))}</span>`
          : '';
        dnInner += `</span><span class="anl-nb-summary-right"><span class="anl-nb-chevron" aria-hidden="true"><i class="bi bi-chevron-down"></i></span></span></summary>`;
        dnInner += `<div class="anl-verbatim-step-inner anl-nb-body anl-nb-body--detail-step">`;

        (step.actions || []).forEach((act) => {
          const v = act.verdict;
          const num =
            act.prompt_step_index != null ? Number(act.prompt_step_index) : act.ordinal || 0;
          const actionLine = `Action ${num} · ${act.label || 'Action'}`;
          const actionTs =
            act.wall_time_sec != null && Number.isFinite(Number(act.wall_time_sec))
              ? formatWallClockDisplay(Number(act.wall_time_sec))
              : '';
          const _logEntries = parseVerbatimLogEntries(act.verbatim_log || '');
          const logCount = _logEntries.length;

          dnInner += `<details class="anl-verbatim-chunk anl-verbatim-chunk-action anl-nb-action" open>`;
          dnInner += `<summary class="anl-nb-summary anl-nb-summary--action">`;
          dnInner += `<span class="anl-nb-summary-main">`;
          dnInner += `<span class="anl-nb-action-title">${escapeHtml(actionLine)}</span>`;
          if (actionTs) {
            dnInner += `<span class="anl-nb-action-ts"><i class="bi bi-clock"></i> ${escapeHtml(actionTs)}</span>`;
          }
          if (logCount) {
            dnInner += `<span class="anl-nb-badge anl-nb-badge--logs">${logCount} log${logCount > 1 ? 's' : ''}</span>`;
          }
          dnInner += `</span><span class="anl-nb-chevron anl-nb-chevron--sm" aria-hidden="true"><i class="bi bi-chevron-down"></i></span></summary>`;
          dnInner += `<div class="anl-nb-body anl-nb-body--action-detail">`;

          if (act.step_context) {
            dnInner += `<p class="anl-nb-action-lead"><strong>Action step:</strong> ${escapeHtml(act.step_context)}</p>`;
          }

          if (v) {
            dnInner += `<div class="anl-nb-verdict">`;
            if (v.evidence != null && v.evidence !== '')
              dnInner += `<div><strong>Evidence:</strong> ${escapeHtml(String(v.evidence))}</div>`;
            if (v.wrong_reason != null && v.wrong_reason !== '')
              dnInner += `<div style="margin-top:0.35rem;"><strong>Wrong reason:</strong> ${escapeHtml(String(v.wrong_reason))}</div>`;
            if (Array.isArray(v.objects_detected) && v.objects_detected.length)
              dnInner += `<div style="margin-top:0.35rem;"><strong>Objects:</strong> ${escapeHtml(v.objects_detected.join(', '))}</div>`;
            if (v.completed === true || v.wrong_action === true || v.confidence != null) {
              dnInner += `<div style="margin-top:0.4rem;display:flex;flex-wrap:wrap;gap:0.35rem;align-items:center;">`;
              if (v.completed === true)
                dnInner += `<span class="anl-nb-pill anl-nb-pill--ok">Completed</span>`;
              if (v.wrong_action === true)
                dnInner += `<span class="anl-nb-pill anl-nb-pill--bad">Wrong action</span>`;
              if (v.confidence != null && !Number.isNaN(Number(v.confidence)))
                dnInner += `<span class="anl-nb-pill anl-nb-pill--muted">Confidence ${normalizeConfidencePct(v.confidence)}%</span>`;
              dnInner += `</div>`;
            }
            dnInner += `</div>`;
          }

          if (_logEntries.length > 0) {
            dnInner += `<div class="anl-nb-log-label"><i class="bi bi-list-ul"></i> Activity log · exact timestamps</div>`;
            dnInner += renderVerbatimLogEntries(_logEntries, escapeHtml, traineeArtifacts, usedArtifactKeys);
          } else if (act.wall_time_sec != null && traineeArtifacts.length) {
            dnInner += renderTraineeArtifactsForTimestamp(
              traineeArtifacts,
              act.wall_time_sec,
              usedArtifactKeys,
              escapeHtml
            );
          } else {
            dnInner += `<div class="anl-nb-empty-logs">No cycle logs recorded for this action step.</div>`;
          }
          dnInner += `</div></details>`;
        });

        dnInner += `</div></details>`;
      });
      const unmatchedArtifacts = traineeArtifacts.filter(
        (a) => !usedArtifactKeys.has(a.key) && a.type !== 'deviation_proof'
      );
      if (unmatchedArtifacts.length) {
        dnInner += `<details class="anl-verbatim-step anl-nb-step anl-nb-step--artifacts">`;
        dnInner += `<summary class="anl-nb-summary anl-nb-summary--detail-step">`;
        dnInner += `<span class="anl-nb-summary-main"><span class="anl-nb-step-num anl-detail-step-chip">Trainee media</span>`;
        dnInner += `<span class="anl-nb-step-snippet">${unmatchedArtifacts.length} capture(s) not linked to a log timestamp</span></span>`;
        dnInner += `<span class="anl-nb-chevron" aria-hidden="true"><i class="bi bi-chevron-down"></i></span></summary>`;
        dnInner += `<div class="anl-verbatim-step-inner anl-nb-body anl-nb-body--detail-step anl-artifact-grid">`;
        unmatchedArtifacts.forEach((artifact) => {
          dnInner += renderTraineeArtifactHtml(artifact, escapeHtml);
        });
        dnInner += `</div></details>`;
      }
      dnInner += '</div>';
    } else if (detailedNb.length > 0) {
      dnInner += '<div class="anl-nb-accordion anl-nb-accordion--detail">';
      detailedNb.forEach((stepEntry) => {
        const sn = stepEntry.step_number;
        const chunks = stepEntry.chunks || [];
        const cycleChunks = chunks.filter((c) => c && c.kind === 'cycle' && c.lines && c.lines.length);
        const firstLine =
          cycleChunks[0] && cycleChunks[0].lines[0]
            ? cycleChunks[0].lines[0]
            : `[STEP ${sn}]`;
        const snippet = truncateText(firstLine, 90);
        let cycNum = 0;
        dnInner += `
          <details class="anl-verbatim-step anl-nb-step">
            <summary class="anl-nb-summary anl-nb-summary--detail-step">
              <span class="anl-nb-summary-main">
                <span class="anl-nb-step-num">Step ${escapeHtml(String(sn))}</span>
                <span class="anl-nb-step-snippet">${escapeHtml(snippet)}</span>
              </span>
              <span class="anl-nb-chevron" aria-hidden="true"><i class="bi bi-chevron-down"></i></span>
            </summary>
            <div class="anl-verbatim-step-inner anl-nb-body anl-nb-body--detail-step">`;
        chunks.forEach((chunk) => {
          if (!chunk || chunk.kind !== 'cycle' || !chunk.lines || !chunk.lines.length) return;
          cycNum += 1;
          const lines = chunk.lines || [];
          const joined = lines.join('\n');
          const sumLine = lines[0] != null ? lines[0] : 'Log segment';
          const n = lines.length;
          const _legacyParsed = parseVerbatimLogEntries(joined);
          const _legacyBody = _legacyParsed.length > 0
            ? `<div class="anl-nb-body anl-nb-body--action-detail">${renderVerbatimLogEntries(_legacyParsed, escapeHtml, traineeArtifacts, usedArtifactKeys)}</div>`
            : `<pre class="anl-verbatim-pre" tabindex="0">${escapeHtml(joined)}</pre>`;
          dnInner += `
            <details class="anl-verbatim-chunk anl-verbatim-chunk-action anl-nb-action">
              <summary class="anl-nb-summary anl-nb-summary--action">
                <span class="anl-nb-summary-main"><span class="anl-nb-action-title">${escapeHtml(`Action ${cycNum} · activity`)}</span><span class="anl-nb-summary-meta anl-nb-summary-meta--chunk">${escapeHtml(truncateText(sumLine, 72))} · ${n} lines</span></span>
                <span class="anl-nb-chevron anl-nb-chevron--sm" aria-hidden="true"><i class="bi bi-chevron-down"></i></span>
              </summary>
              ${_legacyBody}
            </details>`;
        });
        dnInner += `
            </div>
          </details>`;
      });
      dnInner += '</div>';
    }

    if (dnInner) {
      detailedSectionHtml = `
        <div class="anl-section">
          <h3 class="anl-section-title anl-section-collapsible" data-section="enotebook_detail" style="cursor:pointer;user-select:none;">
            <i class="bi bi-journal-code"></i> Detailed e-notebook (parsed)
            <i class="bi bi-chevron-${detOpen ? 'up' : 'down'}" style="float:right;margin-top:0.2rem;"></i>
          </h3>
          <div class="anl-enotebook-detail-body" style="padding:1.25rem 1.5rem 1.5rem;display:${detOpen ? 'block' : 'none'};">${dnInner}
          </div>
        </div>`;
    }

    const gradeColor = { A:'#27ae60', B:'#2196F3', C:'#FF9800', D:'#f39c12', F:'#e74c3c' };
    const gc = gradeColor[finalGrade] || '#e74c3c';

    let html = `<div class="anl-results">`;

    /* ── Overall Performance Cards ── */
    html += `
      <div class="anl-section">
        <h3 class="anl-section-title"><i class="bi bi-bar-chart-fill"></i> Overall Performance</h3>
        <div class="anl-kpi-row">
          <div class="anl-kpi anl-kpi-purple">
            <div class="anl-kpi-icon"><i class="bi bi-award-fill"></i></div>
            <div class="anl-kpi-body">
              <div class="anl-kpi-num">${finalReproScore}<span class="anl-kpi-unit">/100</span></div>
              <div class="anl-kpi-lbl">Reproducibility</div>
              <div class="anl-kpi-badge" style="background:${gc};">Grade ${finalGrade}</div>
            </div>
            </div>
          <div class="anl-kpi anl-kpi-teal">
            <div class="anl-kpi-icon"><i class="bi bi-clock-fill"></i></div>
            <div class="anl-kpi-body">
              <div class="anl-kpi-num">${formatTime(finalTotalTime)}</div>
              <div class="anl-kpi-lbl">Total Time</div>
            </div>
            </div>
          <div class="anl-kpi anl-kpi-blue">
            <div class="anl-kpi-icon"><i class="bi bi-check2-square"></i></div>
            <div class="anl-kpi-body">
              <div class="anl-kpi-num">${finalCompleted}<span class="anl-kpi-unit">/${finalTotal}</span></div>
              <div class="anl-kpi-lbl">Steps Done</div>
            </div>
          </div>
          <div class="anl-kpi ${finalDeviations > 0 ? 'anl-kpi-red' : 'anl-kpi-green'}">
            <div class="anl-kpi-icon"><i class="bi bi-${finalDeviations > 0 ? 'exclamation-triangle-fill' : 'shield-check'}"></i></div>
            <div class="anl-kpi-body">
              <div class="anl-kpi-num">${finalDeviations}</div>
              <div class="anl-kpi-lbl">Deviations</div>
            </div>
          </div>
          <div class="anl-kpi anl-kpi-orange">
            <div class="anl-kpi-icon"><i class="bi bi-graph-up"></i></div>
            <div class="anl-kpi-body">
              <div class="anl-kpi-num">${Math.round(finalConfidence * 100)}%</div>
              <div class="anl-kpi-lbl">AI Confidence</div>
            </div>
            </div>
          </div>
        </div>
    `;
    
    /* ── Reproducibility Score Details ── */
    html += `
      <div class="anl-section">
        <h3 class="anl-section-title"><i class="bi bi-clipboard-data-fill"></i> Reproducibility Score Details</h3>
        <div class="anl-repro-card">
          <div class="anl-repro-score" style="border-color:${gc}; color:${gc};">
            <div class="anl-repro-num">${finalReproScore}</div>
            <div class="anl-repro-grade" style="background:${gc};">Grade ${finalGrade}</div>
          </div>
          <div class="anl-repro-breakdown">
            <div class="anl-repro-item">
              <div class="anl-repro-label"><i class="bi bi-clock"></i> Time Efficiency <span class="anl-repro-weight">(30%)</span></div>
              <div class="anl-repro-bar-wrap">
                <div class="anl-repro-bar" style="width:${Math.round(reproducibility.time_score || 0)}%; background: linear-gradient(90deg,#2196F3,#4fc3f7);"></div>
              </div>
              <div class="anl-repro-score-num">${Math.round(reproducibility.time_score || 0)}/100</div>
            </div>
            <div class="anl-repro-item">
              <div class="anl-repro-label"><i class="bi bi-bullseye"></i> Accuracy <span class="anl-repro-weight">(20%)</span></div>
              <div class="anl-repro-bar-wrap">
                <div class="anl-repro-bar" style="width:${Math.round(reproducibility.accuracy_score || 0)}%; background: linear-gradient(90deg,#FF9800,#ffcc02);"></div>
              </div>
              <div class="anl-repro-score-num">${Math.round(reproducibility.accuracy_score || 0)}/100</div>
            </div>
            <div class="anl-repro-item">
              <div class="anl-repro-label"><i class="bi bi-check-circle-fill"></i> Completion <span class="anl-repro-weight">(50%)</span></div>
              <div class="anl-repro-bar-wrap">
                <div class="anl-repro-bar" style="width:${Math.round(reproducibility.completion_score || 0)}%; background: linear-gradient(90deg,#27ae60,#4CAF50);"></div>
              </div>
              <div class="anl-repro-score-num">${Math.round(reproducibility.completion_score || 0)}/100</div>
            </div>
          </div>
          <div class="anl-repro-validation">
            <div class="anl-validation-label"><i class="bi bi-shield-check"></i> Score Validation</div>
            <div class="anl-validation-text">
    `;
    
    if (gemini.ok && gemini.json && gemini.json.reproducibility_validation) {
      html += escapeHtml(gemini.json.reproducibility_validation);
    } else {
      html += escapeHtml(
        reproducibility.score_validation_fallback ||
        (finalTotal === 0 || finalCompleted === 0
          ? `Score is ${reproducibility.total_score || 0}/100 (Grade ${reproducibility.grade || 'F'}) — no completed steps were recorded.`
          : `Score is ${reproducibility.total_score || 0}/100 (Grade ${reproducibility.grade || 'F'}) based on time efficiency (${Math.round(reproducibility.time_score || 0)}/100 vs reference trainer video), accuracy (${Math.round(reproducibility.accuracy_score || 0)}/100), and completion (${Math.round(reproducibility.completion_score || 0)}/100).`)
      );
    }

    html += `</div></div></div></div>`;

    /* ── E-Notebook Summary ── */
    html += `
      <div class="anl-section">
        <h3 class="anl-section-title anl-section-collapsible" data-section="enotebook" style="cursor:pointer;user-select:none;">
          <i class="bi bi-journal-text"></i> E-Notebook Summary
          <i class="bi bi-chevron-${collapsedSections.enotebook ? 'down' : 'up'}" style="float:right;margin-top:0.2rem;"></i>
        </h3>
        <div class="anl-notebook" style="display:${collapsedSections.enotebook ? 'none' : 'block'};">
          <div class="anl-notebook-header" style="display:flex;align-items:center;gap:1.5rem;padding:0.9rem 1.5rem;background:#f8f9fa;border-bottom:1px solid #e9ecef;font-size:0.9rem;color:#4a5568;flex-wrap:wrap;text-align:left;">
            <span><i class="bi bi-info-circle" style="color:#FF5722;margin-right:0.25rem;"></i> Protocol: <strong style="color:#2d3748;">${escapeHtml(schedule.protocol_name || 'Unknown')}</strong></span>
            <span><i class="bi bi-calendar3" style="color:#FF5722;margin-right:0.25rem;"></i> ${formatDate(schedule.scheduled_date)}</span>
            <span><i class="bi bi-clock" style="color:#FF5722;margin-right:0.25rem;"></i> ${formatTime(finalTotalTime)}</span>
            <span><i class="bi bi-check2-all" style="color:#FF5722;margin-right:0.25rem;"></i> ${finalCompleted}/${finalTotal} steps</span>
          </div>
          <div class="anl-notebook-body" style="text-align:left;">
    `;

    if (gemini.ok && gemini.json && gemini.json.transcription) {
      html += renderNotebookSummaryAsParagraphs(gemini.json.transcription, escapeHtml);
    } else if (structuredSb.length > 0) {
      html += buildNarrativeSummaryFromStructuredNotebook(structuredSb, escapeHtml);
    } else if (metrics.transcription && metrics.transcription.length > 0) {
      html += buildProseFromTranscriptionItems(metrics.transcription, escapeHtml);
      } else {
      const stepItems = collectTranscriptionFromStepStats(metrics.step_stats);
      if (stepItems.length > 0) {
        html += buildProseFromTranscriptionItems(stepItems, escapeHtml);
      } else {
      // Fallback: step-by-step timing only when no transcription text exists
      const stepKeys = Object.keys(metrics.step_stats || {}).sort((a,b) => parseInt(a)-parseInt(b));
      stepKeys.forEach(stepNum => {
        const step = metrics.step_stats[stepNum];
        const done = step.is_completed === true;
        const stepStart = resolveStepStartDisplay(step);
        const stepEnd   = resolveStepEndDisplay(step) || null;
    html += `
          <div style="margin-bottom:0.9rem;border-radius:10px;border-left:4px solid ${done ? '#27ae60' : '#FF9800'};overflow:hidden;text-align:left;">
            <div style="background:#f8f9fa;padding:0.6rem 0.9rem;display:flex;align-items:center;gap:0.6rem;flex-wrap:wrap;font-size:0.9rem;font-weight:600;color:#2d3748;border-bottom:1px solid #e9ecef;">
              <span>${done ? '✅' : '⏳'} Step ${stepNum}</span>
              ${stepStart ? `<span style="font-size:0.8rem;color:#718096;background:#e9ecef;padding:0.15rem 0.5rem;border-radius:6px;flex-shrink:0;"><i class="bi bi-play-fill"></i> Start: ${escapeHtml(stepStart)}</span>` : ''}
              ${stepEnd   ? `<span style="font-size:0.8rem;color:#166534;background:rgba(39,174,96,0.12);padding:0.15rem 0.5rem;border-radius:6px;flex-shrink:0;"><i class="bi bi-stop-fill"></i> End: ${escapeHtml(stepEnd)}</span>` : ''}
              ${resolveStepDurationSec(step) ? `<span style="font-size:0.8rem;color:#4a5568;background:#e2e8f0;padding:0.15rem 0.5rem;border-radius:6px;flex-shrink:0;"><i class="bi bi-stopwatch"></i> ${formatTime(resolveStepDurationSec(step))}</span>` : ''}
          </div>
            <div style="padding:0.5rem 0.75rem;">
              <div style="font-size:0.85rem;color:#a0aec0;font-style:italic;padding:0.35rem 0.5rem;">Step ${done ? 'completed' : 'in progress'} — no transcription recorded</div>
            </div></div>`;
      });
      }
    }

    // Summary banner (inside collapsible E-Notebook body), then close notebook — detailed block is sibling (always visible)
    const bannerBg    = finalDeviations > 0 ? 'rgba(231,76,60,0.08)' : 'rgba(39,174,96,0.1)';
    const bannerColor = finalDeviations > 0 ? '#c53030' : '#166534';
    const bannerBdr   = finalDeviations > 0 ? 'rgba(231,76,60,0.2)' : 'rgba(39,174,96,0.25)';
    html += `
          <div style="margin-top:1rem;padding:0.85rem 1rem;border-radius:10px;font-weight:600;font-size:0.92rem;display:flex;align-items:center;gap:0.55rem;background:${bannerBg};color:${bannerColor};border:1px solid ${bannerBdr};text-align:left;">
            <i class="bi bi-${finalDeviations > 0 ? 'exclamation-triangle-fill' : 'check-circle-fill'}"></i>
            ${finalDeviations > 0 ? `${finalDeviations} deviation(s) detected — see Deviations section below` : 'Experiment completed successfully — no deviations detected'}
          </div>
        </div>
        </div>`;
    html += detailedSectionHtml;
    html += `
      </div>
    `;
    
    /* ── Deviations ── */
    const geminiDeviations = (gemini.ok && gemini.json && gemini.json.deviations) ? gemini.json.deviations : [];
    const metricsDeviations = metrics.deviations || [];
    const allDeviations = [...geminiDeviations];
    const geminiStepMap = new Map(
      geminiDeviations.filter((d) => d.step != null).map((d) => [String(d.step), true])
    );
    metricsDeviations.forEach(dev => {
      const ds = dev.step != null ? String(dev.step) : 'N/A';
      if (!geminiStepMap.has(ds)) allDeviations.push({
        step: ds,
        description: dev.reason || dev.description || 'Action deviated from the expected protocol procedure.',
        impact: dev.evidence || 'This deviation may affect the accuracy or reproducibility of the experiment results.',
        prevention: 'Review the protocol step carefully before execution. Ensure all required materials are prepared and the correct technique is applied as described in the procedure.',
        time: formatWallClockDisplay(dev.time_formatted || (typeof dev.time === 'number' ? dev.time : dev.time || '')),
        time_sec: typeof dev.time_sec === 'number' ? dev.time_sec : (typeof dev.time === 'number' ? dev.time : null),
        confidence: dev.confidence,
        frame_ref: dev.frame_ref || null,
        proof_file: dev.proof_file || null,
        proof_s3_key: dev.proof_s3_key || null,
        proof_url: dev.proof_url || null
      });
    });
    // Ensure Gemini deviations also have fallback impact/prevention + proof fields from metrics
    allDeviations.forEach((dev) => {
      if (!dev.impact || dev.impact.trim() === '')
        dev.impact = 'This deviation may affect the accuracy or reproducibility of the experiment results.';
      if (!dev.prevention || dev.prevention.trim() === '')
        dev.prevention = 'Review the protocol step carefully before execution. Ensure all required materials are prepared and the correct technique is applied as described in the procedure.';
      if (dev.frame_ref || dev.proof_file) return;
      const match = metricsDeviations.find((md) => {
        if (String(md.step) !== String(dev.step)) return false;
        if (dev.time_sec == null || md.time_sec == null) return true;
        return Math.abs(Number(md.time_sec) - Number(dev.time_sec)) <= 3;
      });
      if (!match) return;
      if (!dev.frame_ref && match.frame_ref) dev.frame_ref = match.frame_ref;
      if (!dev.proof_file && match.proof_file) dev.proof_file = match.proof_file;
      if (!dev.proof_s3_key && match.proof_s3_key) dev.proof_s3_key = match.proof_s3_key;
      if (dev.time_sec == null && match.time_sec != null) dev.time_sec = match.time_sec;
    });

    const deviationsWithProof = attachDeviationProofUrls(allDeviations, traineeArtifacts);

    if (deviationsWithProof.length > 0) {
      html += `
        <div class="anl-section">
          <h3 class="anl-section-title anl-section-warn anl-section-collapsible" data-section="deviations" style="font-size:1.1rem;font-weight:700;padding:1rem 1.5rem;border-bottom:2px solid #e74c3c;display:flex;align-items:center;gap:0.55rem;background:linear-gradient(135deg,#fff5f5,white);margin:0;color:#c53030;cursor:pointer;user-select:none;">
            <i class="bi bi-exclamation-triangle-fill" style="color:#e74c3c;"></i> Deviations Analysis
            <span style="background:#e74c3c;color:white;padding:0.2rem 0.65rem;border-radius:20px;font-size:0.75rem;font-weight:700;margin-left:auto;">${deviationsWithProof.length} found</span>
            <i class="bi bi-chevron-${collapsedSections.deviations ? 'down' : 'up'}" style="margin-left:0.5rem;"></i>
          </h3>
          <div style="padding:1.25rem 1.5rem 1.5rem;display:flex;flex-direction:column;gap:0.9rem;display:${collapsedSections.deviations ? 'none' : 'flex'};">
      `;
      html += renderDeviationStepwiseHtml(
        deviationsWithProof,
        structuredSb,
        detailedNb,
        protocolSteps,
        escapeHtml
      );
      html += `</div></div>`;
    } else if (finalDeviations > 0) {
      html += `
        <div class="anl-section">
          <h3 class="anl-section-title anl-section-warn" style="font-size:1.1rem;font-weight:700;padding:1rem 1.5rem;border-bottom:2px solid #e74c3c;display:flex;align-items:center;gap:0.55rem;background:linear-gradient(135deg,#fff5f5,white);margin:0;color:#c53030;">
            <i class="bi bi-exclamation-triangle-fill" style="color:#e74c3c;"></i> Deviations Analysis
          </h3>
          <div style="padding:1.25rem 1.5rem;">
            <div style="border:1px solid #fca5a5;border-left:5px solid #e74c3c;border-radius:12px;background:#fff5f5;padding:0.9rem 1rem;font-size:0.92rem;color:#4a5568;text-align:left;">
              <strong style="color:#c53030;">${finalDeviations} deviation(s)</strong> were detected during this experiment. Detailed information is being processed.
            </div>
          </div>
        </div>
      `;
    }
    
    /* ── Step-by-Step Performance + Time Breakdown ── */
    const stepKeys = metrics.steps || (metrics.step_stats ? Object.keys(metrics.step_stats) : []);
    if (stepKeys.length > 0) {
      const sortedSteps = [...stepKeys].map(s => parseInt(s)).sort((a,b) => a-b);
      
      // Boundary-based step timing (Unity step_start → step_end)
      let stepTimeSum = 0;
      let firstStepStartSec = null;
      let lastStepEndSec = null;
      sortedSteps.forEach(sn => {
        const st = metrics.step_stats && metrics.step_stats[sn];
        if (!st) return;
        stepTimeSum += resolveStepDurationSec(st);
        const bs = st.boundary_start ?? st.first_prompt;
        const be = st.boundary_end ?? st.completion;
        if (bs != null && Number.isFinite(Number(bs))) {
          firstStepStartSec = firstStepStartSec == null ? Number(bs) : Math.min(firstStepStartSec, Number(bs));
        }
        if (be != null && Number.isFinite(Number(be))) {
          lastStepEndSec = lastStepEndSec == null ? Number(be) : Math.max(lastStepEndSec, Number(be));
        }
      });
      const sessionTiming = metrics.session_timing || {};
      const sessionStartDisplay = formatWallClockDisplay(
        sessionTiming.started_formatted || sessionTiming.started_wall_sec || null
      );
      const sessionEndDisplay = formatWallClockDisplay(
        sessionTiming.ended_formatted || sessionTiming.ended_wall_sec || null
      );
      const activeSpanSec =
        firstStepStartSec != null && lastStepEndSec != null && lastStepEndSec >= firstStepStartSec
          ? lastStepEndSec - firstStepStartSec
          : null;
      const setupWrapSec =
        activeSpanSec != null ? Math.max(0, finalTotalTime - activeSpanSec) : Math.abs(finalTotalTime - stepTimeSum);
      const stepsAddUp =
        activeSpanSec != null ? Math.abs(stepTimeSum - activeSpanSec) <= 5 : Math.abs(finalTotalTime - stepTimeSum) <= 5;

      html += `
        <div class="anl-section">
          <h3 class="anl-section-title anl-section-collapsible" data-section="stepPerformance" style="cursor:pointer;user-select:none;">
            <i class="bi bi-list-ol"></i> Step-by-Step Performance
            <i class="bi bi-chevron-${collapsedSections.stepPerformance ? 'down' : 'up'}" style="float:right;margin-top:0.2rem;"></i>
          </h3>
          <div class="anl-steps" style="display:${collapsedSections.stepPerformance ? 'none' : 'block'};">
      `;
      
      sortedSteps.forEach(stepNum => {
        const step = (metrics.step_stats && metrics.step_stats[stepNum]) ? metrics.step_stats[stepNum] : {};
        const done     = step.is_completed === true;
        const stColor  = done ? '#27ae60' : '#d97706';
        const devCount = step.actual_deviations || step.deviations || 0;
        const stepStart = resolveStepStartDisplay(step);
        const stepEnd   = resolveStepEndDisplay(step) || null;
        const stepDur   = resolveStepDurationSec(step);
        // Legacy txt counted each Gemini call as a prompt; JSONL once stored unique action_prompts only.
        const promptCount = stepPromptCount(step);
        const stepTimeEff = done && step.time_efficiency_score != null ? Math.round(step.time_efficiency_score) : null;
        const stepRefTime = step.reference_time != null ? Number(step.reference_time) : null;
        
        html += `
          <div class="anl-step-card ${done ? 'anl-step-done' : 'anl-step-pending'}">
            <div class="anl-step-header">
              <span class="anl-step-title" style="color:${stColor};">
                ${done ? '✅' : '⏳'} Step ${stepNum}: ${done ? 'Completed' : 'In Progress'}
              </span>
              <span class="anl-step-badge ${devCount > 0 ? 'anl-step-badge-warn' : 'anl-step-badge-ok'}">
                ${devCount > 0 ? `⚠️ ${devCount} deviation${devCount>1?'s':''}` : '✓ Clean'}
              </span>
            </div>
            ${(stepStart || stepEnd) ? `
            <div style="padding:0.5rem 1rem;background:rgba(0,0,0,0.02);display:flex;gap:0.75rem;flex-wrap:wrap;border-bottom:1px solid rgba(0,0,0,0.05);font-size:0.83rem;">
              ${stepStart ? `<span style="color:#718096;display:inline-flex;align-items:center;gap:0.25rem;"><i class="bi bi-play-fill" style="color:#27ae60;"></i> Start: <strong>${escapeHtml(stepStart)}</strong></span>` : ''}
              ${stepEnd   ? `<span style="color:#718096;display:inline-flex;align-items:center;gap:0.25rem;"><i class="bi bi-stop-fill"  style="color:#e74c3c;"></i> End: <strong>${escapeHtml(stepEnd)}</strong></span>` : ''}
              <span style="color:#2d3748;font-weight:700;display:inline-flex;align-items:center;gap:0.25rem;"><i class="bi bi-stopwatch" style="color:#FF5722;"></i> Duration: ${formatTime(stepDur)}</span>
            </div>` : ''}
            <div class="anl-step-metrics">
              <div class="anl-step-metric">
                <div class="anl-step-metric-lbl"><i class="bi bi-stopwatch"></i> Duration</div>
                <div class="anl-step-metric-val">${formatTime(stepDur)}</div>
              </div>
              <div class="anl-step-metric">
                <div class="anl-step-metric-lbl"><i class="bi bi-chat-dots"></i> Prompts</div>
                <div class="anl-step-metric-val">${promptCount}</div>
              </div>
              <div class="anl-step-metric">
                <div class="anl-step-metric-lbl"><i class="bi bi-reply-all"></i> Responses</div>
                <div class="anl-step-metric-val">${step.responses || 0}</div>
              </div>
              <div class="anl-step-metric">
                <div class="anl-step-metric-lbl"><i class="bi bi-graph-up"></i> Confidence</div>
                <div class="anl-step-metric-val" style="color:${(step.avg_confidence||0)>=0.8?'#27ae60':(step.avg_confidence||0)>=0.5?'#d97706':'#e74c3c'};">${normalizeConfidencePct(step.avg_confidence) ?? 0}%</div>
              </div>
              ${stepTimeEff != null && stepRefTime ? `
              <div class="anl-step-metric">
                <div class="anl-step-metric-lbl"><i class="bi bi-camera-video"></i> Time Score</div>
                <div class="anl-step-metric-val" style="color:${stepTimeEff >= 100 ? '#27ae60' : '#d97706'};" title="Reference video: ${formatTime(stepRefTime)}">${stepTimeEff}/100</div>
              </div>` : ''}
              <div class="anl-step-metric ${devCount > 0 ? 'anl-step-metric-warn' : ''}">
                <div class="anl-step-metric-lbl"><i class="bi bi-exclamation-circle"></i> Deviations</div>
                <div class="anl-step-metric-val" style="color:${devCount>0?'#e74c3c':'#27ae60'};">${devCount}</div>
              </div>
            </div>
          </div>
        `;
      });
      
      // Time validation summary (Unity-recorded session + step boundaries)
      html += `
          </div>
          <div style="margin:0 1.5rem 1.5rem;padding:1rem 1.25rem;background:${stepsAddUp?'rgba(39,174,96,0.07)':'rgba(255,152,0,0.07)'};border-radius:12px;border:1px solid ${stepsAddUp?'rgba(39,174,96,0.25)':'rgba(255,152,0,0.3)'};font-size:0.9rem;text-align:left;">
            <div style="font-weight:700;color:${stepsAddUp?'#166534':'#92400e'};margin-bottom:0.6rem;display:flex;align-items:center;gap:0.4rem;">
              <i class="bi bi-${stepsAddUp?'check-circle-fill':'exclamation-triangle-fill'}"></i>
              Time Verification ${stepsAddUp ? '— Step boundaries confirmed ✓' : '— Review step boundary times'}
            </div>
            <div style="display:flex;gap:1.5rem;flex-wrap:wrap;color:#4a5568;">
              ${sessionStartDisplay ? `<span><i class="bi bi-play-circle" style="color:#27ae60;"></i> Experiment started: <strong>${escapeHtml(sessionStartDisplay)}</strong></span>` : ''}
              ${sessionEndDisplay ? `<span><i class="bi bi-stop-circle" style="color:#e74c3c;"></i> Experiment ended: <strong>${escapeHtml(sessionEndDisplay)}</strong></span>` : ''}
              <span><i class="bi bi-sigma" style="color:#FF5722;"></i> Sum of step durations: <strong>${formatTime(stepTimeSum)}</strong></span>
              ${activeSpanSec != null ? `<span><i class="bi bi-arrows-expand" style="color:#805ad5;"></i> First step start → last step end: <strong>${formatTime(activeSpanSec)}</strong></span>` : ''}
              <span><i class="bi bi-clock-history" style="color:#2196F3;"></i> Total experiment time: <strong>${formatTime(finalTotalTime)}</strong></span>
              ${setupWrapSec > 5 ? `<span style="color:#92400e;"><i class="bi bi-info-circle"></i> Setup & wrap-up (before step 1 / after last step): <strong>${formatTime(setupWrapSec)}</strong></span>` : ''}
            </div>
            <div style="margin-top:0.5rem;font-size:0.82rem;color:#718096;">Step start/end times are recorded by Unity when each step begins and ends. Total time is recorded when the experiment starts and finishes.</div>
          </div>
        </div>
      `;
    } else {
      html += '';
    }
    
    html += `</div>`;
    
    // Use React state — no direct DOM manipulation (avoids React re-render clearing innerHTML)
    setAnalyticsHtml(html);
    setShowRightPanel(true);
  };
  
  // Handle collapsible sections - re-render when state changes
  useEffect(() => {
    if (currentAnalyticsData && showRightPanel) {
      // Use a timeout to avoid infinite loops
      const timer = setTimeout(() => {
        displayAnalytics(currentAnalyticsData);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [collapsedSections, showRightPanel]);
  
  // Add click handlers for collapsible sections + proof lightbox
  useEffect(() => {
    if (!showRightPanel) return;
    
    const handleCollapse = (e) => {
      const proofBtn = e.target.closest && e.target.closest('[data-proof-url]');
      if (proofBtn) {
        e.preventDefault();
        e.stopPropagation();
        const url = proofBtn.getAttribute('data-proof-url');
        if (!url) return;
        let overlay = document.getElementById('anl-proof-lightbox');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.id = 'anl-proof-lightbox';
          overlay.className = 'anl-proof-lightbox';
          overlay.innerHTML = `
            <button type="button" class="anl-proof-lightbox-close" aria-label="Close">&times;</button>
            <img class="anl-proof-lightbox-img" alt="Deviation proof enlarged" />
          `;
          document.body.appendChild(overlay);
          overlay.addEventListener('click', (ev) => {
            if (ev.target === overlay || ev.target.classList.contains('anl-proof-lightbox-close')) {
              overlay.classList.remove('is-open');
              overlay.querySelector('img').removeAttribute('src');
            }
          });
        }
        const img = overlay.querySelector('.anl-proof-lightbox-img');
        if (img) img.src = url;
        overlay.classList.add('is-open');
        return;
      }

      let target = e.target;
      while (target && target !== document.body) {
        if (target.classList && target.classList.contains('anl-section-collapsible')) {
          e.preventDefault();
          e.stopPropagation();
          
          const section = target.getAttribute('data-section');
          if (section) {
            setCollapsedSections(prev => ({
              ...prev,
              [section]: !prev[section]
            }));
          }
          return;
        }
        target = target.parentElement;
      }
    };
    
    document.addEventListener('click', handleCollapse);
    
    return () => {
      document.removeEventListener('click', handleCollapse);
      const overlay = document.getElementById('anl-proof-lightbox');
      if (overlay) overlay.remove();
    };
  }, [showRightPanel]);

  const handleResultsFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentAnalysis) return;
    setUploadLoading(true);
    setUploadStatus('uploading');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schedule_id', currentAnalysis.scheduleId || '');
      formData.append('experiment_id', currentAnalysis.experimentId);
      formData.append('message', 'Analyze this results file');
      if (currentAnalyticsData) formData.append('analyticsData', JSON.stringify([currentAnalyticsData]));
      const response = await API.post('/qurator/analytics/upload-results', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.ok) {
        addMessageToChat('bot', response.data.is_relevant && response.data.results_saved
          ? `✅ **File Saved!**\n\n${response.data.response}`
          : `📊 **Results:** ${response.data.response}`);
        setUploadStatus('');
      } else throw new Error(response.data.error || 'Upload failed');
    } catch (error) {
      setUploadStatus(`error:${error.message || 'Upload failed'}`);
      setTimeout(() => setUploadStatus(''), 5000);
    } finally {
      setUploadLoading(false);
    }
    event.target.value = '';
  };

  const chatMessagesRef = React.useRef(null);
  const addMessageToChat = (sender, text) => {
    setChatContext(prev => [...prev, { sender, text }]);
  };

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
  }, [chatContext]);

  const sendChatMessage = async () => {
    const message = chatInput.trim();
    if (!message || chatLoading) return;
    setChatInput('');
    addMessageToChat('user', message);
    setChatLoading(true);
    try {
      let experimentContext = {};
      if (currentAnalyticsData) {
        const d = currentAnalyticsData;
        const s = d.schedule || {}, m = d.metrics || {}, r = d.reproducibility || {}, sum = m.summary || {};
        experimentContext = {
          protocol_name: s.protocol_name, scheduled_date: s.scheduled_date,
          total_time: sum.total_time || m.total_time, total_steps: sum.total_steps || m.steps?.length,
          completed_steps: sum.completed_steps, deviations_count: sum.actual_deviations || m.actual_deviations,
          avg_confidence: sum.avg_confidence || m.avg_confidence,
          reproducibility_score: r.total_score || r.score, reproducibility_grade: r.grade,
          time_score: r.time_score, accuracy_score: r.accuracy_score, completion_score: r.completion_score,
          steps_data: m.step_stats, deviations: m.deviations, transcription: m.transcription, gemini_analysis: d.gemini?.json
        };
      }
      const response = await API.post('/qurator/analytics/chat', {
        message, full_context: JSON.stringify(experimentContext),
        chat_history: chatContext, analyticsData: currentAnalyticsData ? [currentAnalyticsData] : null,
        project_id: currentAnalysis?.projectId || null, schedule_id: currentAnalysis?.scheduleId || null,
        experiment_context: experimentContext
      });
      addMessageToChat('bot', response.data.ok ? response.data.response : `⚠️ Error: ${response.data.error || 'Failed'}`);
    } catch (error) {
      addMessageToChat('bot', `⚠️ Error: ${error.message || 'Failed to send'}`);
    } finally {
      setChatLoading(false);
    }
  };

  const closeAnalyzeModal = () => {
    setShowAnalyzeModal(false);
    setCurrentAnalysis(null);
    setAnalyticsHtml('');
    setAnalyticsError('');
    setShowRightPanel(false);
    setModalProtocolName('Experiment Analysis');
    setUploadStatus('');
    setUploadLoading(false);
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="anl-page">
          <div className="anl-loading"><div className="qrt-loading"></div><p>Loading Analytics...</p></div>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="anl-page">

        {/* ── Hero ── */}
        <div className="anl-hero">
          <div className="anl-hero-inner">
            <div>
              <h1 className="anl-hero-title"><i className="bi bi-graph-up-arrow"></i> Experiment Analytics</h1>
              <p className="anl-hero-sub">Comprehensive AI-powered analysis and insights from your laboratory experiments</p>
          </div>
            <Tooltip text="Refresh analytics data">
              <button className="anl-hero-btn" onClick={fetchAnalyticsData}>
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </button>
            </Tooltip>
            </div>
            </div>
            
        {/* ── Performance Stats ── */}
        <div className="row g-3 mb-4">
          {[
            { label:'Total Projects', val: personalStats.total_projects, icon:'bi-folder-fill', cls:'anl-stat-orange', tip:'Your total research projects' },
            { label:'Completed',      val: personalStats.completed,      icon:'bi-check-circle-fill', cls:'anl-stat-green', tip:'Successfully completed experiments' },
            { label:'Scheduled',      val: personalStats.scheduled,      icon:'bi-calendar-check-fill', cls:'anl-stat-blue', tip:'Awaiting execution' },
            { label:'In Progress',    val: personalStats.in_progress,    icon:'bi-hourglass-split', cls:'anl-stat-amber', tip:'Currently running experiments' },
            { label:'Published',      val: personalStats.published,      icon:'bi-broadcast', cls:'anl-stat-purple', tip:'Published and available to Qure users' },
            { label:'Org Users',      val: personalStats.org_users,      icon:'bi-people-fill', cls:'anl-stat-teal', tip:`Team members in ${orgStats.org_name}` },
          ].map((s, i) => (
            <div key={i} className="col-6 col-md-4 col-lg-2">
              <Tooltip text={s.tip} position="bottom">
                <div className={`anl-stat ${s.cls}`}>
                  <div className="anl-stat-ico"><i className={`bi ${s.icon}`}></i></div>
                  <div className="anl-stat-num">{s.val}</div>
                  <div className="anl-stat-lbl">{s.label}</div>
            </div>
              </Tooltip>
            </div>
          ))}
            </div>
            
        {/* ── Tabs ── */}
        <div className="anl-tabs">
          <button className={`anl-tab ${activeTab === 'scheduled' ? 'active' : ''}`} onClick={() => setActiveTab('scheduled')}>
            <i className="bi bi-calendar-week"></i> Scheduled Experiments
          </button>
          <button className={`anl-tab ${activeTab === 'self-consumed' ? 'active' : ''}`} onClick={() => setActiveTab('self-consumed')}>
            <i className="bi bi-person-video3"></i> Self Executed
          </button>
        </div>

        {/* ── Scheduled Tab ── */}
        {activeTab === 'scheduled' && (
          projects.length > 0 ? projects.map(project => (
            <div key={project.project_id} className="anl-project-card">
              <div className="anl-project-head">
                <h2 className="anl-project-title"><i className="bi bi-folder2-open"></i> {project.project_name}</h2>
                  </div>
              <div className="anl-table-wrap">
                <table className="anl-table">
                      <thead>
                        <tr>
                      <th><i className="bi bi-calendar3"></i> Date</th>
                      <th><i className="bi bi-file-text"></i> Protocol</th>
                      <th><i className="bi bi-person"></i> User</th>
                      <th><i className="bi bi-file-earmark-code"></i> Log File</th>
                      <th><i className="bi bi-lightning-charge"></i> Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {project.experiments?.map(exp => (
                          <tr key={exp.schedule_id}>
                            <td>{formatDate(exp.scheduled_date)}</td>
                        <td className="anl-td-strong">{exp.protocol_name}</td>
                        <td>{exp.username || `${exp.first_name || ''} ${exp.last_name || ''}`.trim()}</td>
                        <td className="anl-td-file">{exp.filename || <span className="anl-no-file">No log</span>}</td>
                            <td>
                          <Tooltip text={exp.filename ? 'Run AI analysis on this experiment' : 'No log file available'}>
                              <button 
                              className={`anl-btn-analyze ${!exp.filename ? 'disabled' : ''}`}
                              onClick={() => exp.filename && handleAnalyze(
                                exp.schedule_id,
                                exp.experiment_id,
                                exp.filename,
                                project.project_id,
                                false,
                                false,
                                exp.protocol_name
                              )}
                              disabled={!exp.filename}
                              >
                              <i className="bi bi-cpu"></i> Analyze
                              </button>
                          </Tooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
          )) : (
            <div className="anl-empty">
              <i className="bi bi-bar-chart"></i>
              <h4>No Experiments Available</h4>
              <p>Complete some experiments in your projects to see analytics here.</p>
                </div>
          )
        )}

        {/* ── Self Consumed Tab ── */}
        {activeTab === 'self-consumed' && (
          selfConsumedFiles.length > 0 ? (
            <div className="anl-project-card">
              <div className="anl-project-head">
                <h2 className="anl-project-title"><i className="bi bi-person-video3"></i> Self Executed Experiments</h2>
                </div>
              <div className="anl-table-wrap">
                <table className="anl-table">
                    <thead>
                      <tr>
                      <th><i className="bi bi-calendar3"></i> Date & Time</th>
                      <th><i className="bi bi-file-text"></i> Protocol</th>
                      <th><i className="bi bi-file-earmark-code"></i> Log File</th>
                      <th><i className="bi bi-lightning-charge"></i> Action</th>
                      </tr>
                    </thead>
                    <tbody>
                    {selfConsumedFiles.map((file, idx) => (
                      <tr key={idx}>
                          <td>{formatDateTime(file.created_date)}</td>
                        <td className="anl-td-strong">{file.protocol_name}</td>
                        <td className="anl-td-file">{file.filename}</td>
                          <td>
                          <Tooltip text="Run AI analysis on this experiment">
                            <button className="anl-btn-analyze" onClick={() => handleAnalyze(
                              null,
                              file.experiment_id,
                              file.filename,
                              null,
                              true,
                              false,
                              file.protocol_name
                            )}>
                              <i className="bi bi-cpu"></i> Analyze
                            </button>
                          </Tooltip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
            <div className="anl-empty">
              <i className="bi bi-person-video3"></i>
              <h4>No Self Executed Experiments</h4>
              <p>Self-executed experiments will appear here.</p>
                </div>
          )
        )}

        {/* ── Executive Summary ── */}
        <div className="anl-executive">
          <div className="anl-exec-icon"><i className="bi bi-bar-chart-line-fill"></i></div>
          <div>
            <h4 className="anl-exec-title">Executive Summary</h4>
            <p className="anl-exec-text">
            Welcome, <strong>{user?.username || user?.first_name || 'User'}</strong>! 
              You have completed <strong>{personalStats.completed} experiments</strong> across <strong>{personalStats.total_projects} projects</strong>.
              Your organization <strong>{orgStats.org_name}</strong> has <strong>{orgStats.total_users} active users</strong> with a total of <strong>{orgStats.completed_experiments} completed experiments</strong>.
          </p>
          </div>
        </div>

        {/* ════════ ANALYZE MODAL ════════ */}
        {showAnalyzeModal && (
          <div className="anl-modal-overlay">
            <div className="anl-modal-header">
              <div className="anl-modal-header-main">
                <h2 className="anl-modal-heading">
                  <i className="bi bi-cpu"></i> {modalProtocolName}
                </h2>
              </div>
              <div className="anl-modal-header-actions">
                {showRightPanel && !analyzeLoading && (
                  <>
                    <Tooltip text="Download a complete PDF report with scores, summary, and step performance">
                      <button
                        type="button"
                        className="anl-btn-export-pdf"
                        onClick={handleExportPdf}
                        disabled={exportPdfLoading}
                      >
                        <i className={`bi ${exportPdfLoading ? 'bi-arrow-repeat anl-spin' : 'bi-file-earmark-pdf'}`}></i>
                        {exportPdfLoading ? 'Generating…' : 'Export PDF'}
                      </button>
                    </Tooltip>
                    <Tooltip text="Re-run analysis and replace cached DB results">
                      <button type="button" className="anl-btn-reprocess" onClick={handleReprocess}>
                        <i className="bi bi-arrow-repeat"></i> Reprocess
                      </button>
                    </Tooltip>
                  </>
                )}
                <Tooltip text="Close analysis">
                  <button className="anl-modal-close" onClick={closeAnalyzeModal}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </Tooltip>
              </div>
            </div>
            
            <div className="anl-modal-body">
              <div className="anl-main-panel">

                {/* Loading progress */}
                {analyzeLoading && (
                  <div className="anl-progress-wrap">
                    <div className="anl-spinner"></div>
                    <div className="anl-progress-msg">{processingProgress.message || 'Processing experiment...'}</div>
                    <div className="anl-progress-detail">{processingProgress.details || 'Analyzing log file with AI...'}</div>
                    <div className="anl-progress-bar-track">
                      <div className="anl-progress-bar-fill" style={{ width: `${processingProgress.percentage}%` }}>
                        {processingProgress.percentage > 12 ? `${processingProgress.percentage}%` : ''}
                      </div>
                      </div>
                    <div className="anl-progress-phases">
                      <span className={processingProgress.phase === 'checking'  ? 'active' : ''}>Check Cache</span>
                      <span>›</span>
                      <span className={processingProgress.phase === 'fetching'  ? 'active' : ''}>Fetch Log</span>
                      <span>›</span>
                      <span className={processingProgress.phase === 'parsing'   ? 'active' : ''}>Parse</span>
                      <span>›</span>
                      <span className={processingProgress.phase === 'analyzing' ? 'active' : ''}>AI Analysis</span>
                      <span>›</span>
                      <span className={processingProgress.phase === 'complete'  ? 'active' : ''}>Complete</span>
                        </div>
                      </div>
                )}
                      
                {/* Error state */}
                {!analyzeLoading && analyticsError && (
                  <div className="anl-error-box">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span><strong>Error:</strong> {analyticsError}</span>
                        </div>
                      )}
                      
                {/* Analytics results rendered via state (dangerouslySetInnerHTML avoids React re-render clearing) */}
                {!analyzeLoading && analyticsHtml && (
                  <div dangerouslySetInnerHTML={{ __html: analyticsHtml }} />
                )}

                  </div>

              {/* Right Panel — shown once analysis complete */}
              <div className="anl-right-panel" style={{ display: showRightPanel ? 'flex' : 'none' }}>

                {/* Upload Results */}
                <div className="anl-panel-card">
                  <div className="anl-panel-head"><i className="bi bi-cloud-upload"></i> Upload Results</div>
                  <div className="anl-panel-body">
                    <label
                      className={`anl-upload-zone ${uploadLoading ? 'uploading' : ''}`}
                      style={{ cursor: uploadLoading ? 'wait' : 'pointer' }}
                    >
                      <i className="bi bi-file-earmark-arrow-up anl-upload-icon"></i>
                      {uploadLoading
                        ? <div className="anl-upload-text">📤 Uploading and analyzing...</div>
                        : <><div className="anl-upload-text"><strong>Drop files here</strong> or click to browse</div>
                           <div className="anl-upload-hint">CSV, Excel, PDF, Images</div></>
                      }
                      <input type="file" style={{ display: 'none' }}
                      accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.gif,image/*"
                        onChange={(e) => handleResultsFileSelect(e)} />
                    </label>
                    {uploadStatus.startsWith('error:') && (
                      <div className="anl-upload-error">❌ {uploadStatus.replace('error:', '')}</div>
                    )}
                  </div>
                </div>

                {/* AI Chatbot */}
                <div className="anl-panel-card anl-chat-card">
                  <div className="anl-panel-head">
                    <span><i className="bi bi-robot"></i> AI Assistant</span>
                    <Tooltip text="Clear chat history">
                      <button className="anl-clear-chat" onClick={() => setChatContext([])}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </Tooltip>
                  </div>
                  <div className="anl-panel-body anl-chat-body">
                    <div ref={chatMessagesRef} className="anl-chat-messages">
                      <div className="anl-chat-bubble anl-bubble-bot">
                        <div className="anl-chat-msg-content">
                          <strong>Hello!</strong> I can help you analyze this experiment, generate research content, or answer questions about the data.
                        </div>
                      </div>
                      {chatContext.map((msg, i) => (
                        <div key={i} className={`anl-chat-bubble ${msg.sender === 'user' ? 'anl-bubble-user' : 'anl-bubble-bot'}`}>
                          <div className="anl-chat-msg-content" dangerouslySetInnerHTML={{
                            __html: msg.text
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\*(.*?)\*/g, '<em>$1</em>')
                              .replace(/`(.*?)`/g, '<code>$1</code>')
                              .replace(/\n/g, '<br>')
                          }} />
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="anl-chat-bubble anl-bubble-bot anl-typing">
                          <span></span><span></span><span></span>
                    </div>
                      )}
                    </div>
                    <div className="anl-chat-input-row">
                      <textarea 
                        className="anl-chat-input"
                        placeholder="Ask about this experiment..." 
                        rows="1"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                      />
                      <Tooltip text="Send message">
                        <button className="anl-chat-send" onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>
                          <i className="bi bi-send-fill"></i>
                      </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </QuratorLayout>
  );
};

export default Analytics;
